import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { Alert, Box, Divider, Menu, MenuItem, Snackbar, SpeedDial, SpeedDialAction, SpeedDialIcon, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { emphasize, useTheme } from '@mui/material/styles';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import TitleIcon from '@mui/icons-material/Title';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import { useRetroActionsRegistration } from '../RetroActionsContext';
import { v4 as uuidv4 } from 'uuid';
import {
  CanvasItemMove,
  CanvasItemsBroughtToFront,
  CanvasItemsDeleted,
  CanvasItemsMoveCommitted,
  CanvasItemsMovedDelta,
  CanvasSnapshot,
  ItemLock,
  ItemLocksChanged,
  SetRetroTemplateRequest,
  StickyState,
  TextLabelState,
} from '../../../api/Requests';
import StickyNote, { STICKY_COLORS } from './StickyNote';
import TextLabel, { LABEL_COLORS_LIGHT, LABEL_COLORS_DARK, labelColorStripAnchorSize } from './TextLabel';
import CursorOverlay, { CursorOverlayHandle } from './CursorOverlay';
import {
  applyLiveItemPosition,
  applyLiveItemPositions,
  applyRemoteLiveDelta,
  applyRemoteLivePositions,
  clearLiveDragIds,
  clearLiveItemPosition,
  mergeLiveDragPositions,
  unionIdSets,
} from './liveDragPosition';
import TemplateLayer from './TemplateLayer';
import TemplatePickerDialog from './TemplatePickerDialog';
import {
  countExportItems,
  groupForExport,
  renderSectionsAsMarkdown,
  renderSectionsAsPlainText,
  LabelDimensions,
} from './exportUtils';
import { buildExportFilename, exportItemsAsImage, TemplateExportInfo } from './imageExportUtils';
import { getTemplate, isValidTemplateId } from '../templates';
import { templateBoardBounds } from '../templates/zoneHitTest';
import { useRetroSync } from '../../../hooks/useRetroSync';
import { useCanvasHistory } from '../../../hooks/useCanvasHistory';
import { HttpClient } from '../../../api/HttpClient';
import { UserContext } from '../../../contexts/UserContext';
import { RetroCategory, RetroAction } from '../analytics/RetroTracking';
import { useTracking } from '../../../analytics/useTracking';
import {
  boardToScreen,
  CANVAS_SIZE,
  clampPan,
  clampTextLength,
  applyZIndexSequence,
  computeBringToFrontOrder,
  computeCentreTransform,
  computeFitTransform,
  computeZoomFactor,
  itemZIndex,
  loadViewport,
  MAX_LABEL_CONTENT_LENGTH,
  MAX_STICKY_CONTENT_LENGTH,
  MAX_STICKIES,
  MAX_LABELS,
  diffCanvasRestore,
  mergeAllById,
  mergeById,
  nextZIndex,
  wouldExceedCanvasCaps,
  POINTER_DRAG_THRESHOLD_PX,
  rectsIntersect,
  saveViewport,
  screenToBoard,
  stickyDisplaySize,
  StoredViewport,
} from '../../../utils/canvasUtils';
import {
  DEFAULT_STICKY_SIZE,
  MIN_STICKY_SIZE,
  MAX_STICKY_SIZE,
  SIZE_STEP,
  DEFAULT_LABEL_FONT,
  MIN_LABEL_FONT,
  MAX_LABEL_FONT,
  LABEL_FONT_STEP,
  ResizeCorner,
  ResizeDragState,
  computeResize,
  computeLabelFontResize,
} from '../../../utils/stickyResize';
import { ItemResizeHandleProps } from './ResizeCornerHandles';
import {
  applyLocksChanged,
  applyLocksSnapshot,
  createOccupancyTracker,
  diffOccupancySelection,
  isOccupiedByOther,
  occupiedByName,
  OccupancyMap,
  OCCUPANCY_DENY_TOAST,
  restoreTouchesForeignLock,
} from './occupancyLocks';

const VIEWPORT_SAVE_DEBOUNCE_MS = 400;
/** Board-space pixels added per repeated paste so items don't stack exactly on top. */
const PASTE_BOARD_OFFSET = 30;


export interface CanvasSurfaceProps {
  roomUrlId: string;
  playerId: string;
  playerName: string;
  isModerator: boolean;
  initialRoomState: string;
  initialTemplateId: string | null;
  initialStickies: StickyState[];
  initialTextLabels: TextLabelState[];
}

/** Fixed-position color-swatch strip that appears below a selected canvas item. */
function CanvasColorStrip({
  stripX, stripY, colors, selectedSlot, onChange, isTransforming,
  bg, border,
}: {
  stripX: number;
  stripY: number;
  colors: readonly string[];
  /** Index of the currently active color slot (0–5). */
  selectedSlot: number;
  /** Called with the palette slot index when a dot is clicked. */
  onChange: (slot: number) => void;
  isTransforming: boolean;
  bg: string;
  border: string;
}) {
  return (
    <Box
      sx={{
        position:  'fixed',
        left:       stripX,
        top:        stripY,
        transform: 'translateX(-50%)',
        display:   'flex',
        gap:       '6px',
        padding:   '6px 10px',
        background: bg,
        borderRadius: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        zIndex:    500,
        pointerEvents: isTransforming ? 'none' : 'auto',
        opacity:   isTransforming ? 0 : 1,
        transition: isTransforming ? 'none' : 'opacity 0.2s ease-out',
      }}
    >
      {colors.map((color, i) => (
        <Box
          key={color}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange(i)}
          title={`Color ${i + 1}`}
          sx={{
            width:  22,
            height: 22,
            borderRadius: '50%',
            backgroundColor: color,
            border: i === selectedSlot
              ? '2px solid rgba(0,0,0,0.5)'
              : `2px solid ${border}`,
            boxShadow: i === selectedSlot ? 'inset 0 0 0 2px #fff' : 'none',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'transform 0.1s',
            '&:hover': { transform: 'scale(1.2)' },
          }}
        />
      ))}
    </Box>
  );
}

/** Tooltip label for a SpeedDial action — shows the action name and its keyboard shortcut. */
function SpeedDialLabel({ label, shortcut }: { label: string; shortcut: string }) {
  return (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      {label}
      <Box
        component="span"
        sx={{
          display:      'inline-flex',
          alignItems:   'center',
          justifyContent: 'center',
          width:        18,
          height:       18,
          borderRadius: '4px',
          background:   'rgba(255,255,255,0.2)',
          fontSize:     '11px',
          fontWeight:   700,
          lineHeight:   1,
          flexShrink:   0,
        }}
      >
        {shortcut}
      </Box>
    </Box>
  );
}

/** Keep focus on a contenteditable sticky/label until click completes (avoids blur → re-render swallowing the gesture). */
function preventSpeedDialFocusSteal(e: React.PointerEvent) {
  if (e.button === 0) e.preventDefault();
}

function blurActiveContentEditable() {
  queueMicrotask(() => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.isContentEditable) {
      active.blur();
    }
  });
}

export default function CanvasSurface({
  roomUrlId,
  playerId,
  playerName,
  isModerator,
  initialRoomState,
  initialTemplateId,
  initialStickies,
  initialTextLabels,
}: CanvasSurfaceProps): ReactElement {
  const theme = useTheme();
  const { canvas: ct } = theme;
  const { authToken } = useContext(UserContext);
  const { track } = useTracking();
  const isDarkMode = theme.palette.mode === 'dark';
  // Label and sticky color palettes for the current theme mode — used by the
  // color-strip picker so dots always show the colours visible on this canvas.
  const labelColors = isDarkMode ? LABEL_COLORS_DARK : LABEL_COLORS_LIGHT;

  const [stickies, setStickies] = useState<StickyState[]>(initialStickies);
  const [textLabels, setTextLabels] = useState<TextLabelState[]>(initialTextLabels);
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);
  const [labelEditingId, setLabelEditingId] = useState<string | null>(null);
  const [labelDraggingId, setLabelDraggingId] = useState<string | null>(null);
  const [labelEditTriggerId, setLabelEditTriggerId] = useState<string | null>(null);
  const [labelEditHasText, setLabelEditHasText] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const speedDialRef = useRef<HTMLDivElement>(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(initialTemplateId);
  const templateIdRef = useRef<string | null>(initialTemplateId);
  templateIdRef.current = templateId;
  const [placementMode, setPlacementMode] = useState<'sticky' | 'label' | null>(null);
  const placementModeRef = useRef<'sticky' | 'label' | null>(null);
  placementModeRef.current = placementMode;
  const textLabelsRef = useRef(textLabels);
  // DOM refs for text label elements — used to measure actual fit-content dimensions
  const labelRefsRef    = useRef<Map<string, HTMLDivElement>>(new Map());
  // DOM refs for sticky note elements — used for image export and live-drag paint
  const stickyRefsRef   = useRef<Map<string, HTMLElement>>(new Map());
  // Items this client is mid-drag. Live x/y stay in refs + DOM until drop
  // (see liveDragPosition.ts). A parent re-render must not clobber them.
  const localDragStickyIdsRef = useRef<Set<string>>(new Set());
  const localDragLabelIdsRef  = useRef<Set<string>>(new Set());
  // Ids another client is mid-drag: React x/y stays stale until their commit.
  const remoteDragStickyIdsRef = useRef<Set<string>>(new Set());
  const remoteDragLabelIdsRef  = useRef<Set<string>>(new Set());
  const occupancySessionRef = useRef(createOccupancyTracker());
  const [occupancyAbortEpoch, setOccupancyAbortEpoch] = useState(0);
  const gestureOriginRef = useRef<{ stickies: StickyState[]; labels: TextLabelState[] } | null>(null);
  const groupMovingRef = useRef(false);
  const groupBoundsElRef = useRef<HTMLDivElement | null>(null);
  const groupBoxStartRef = useRef<{ x: number; y: number } | null>(null);
  // DOM ref for the template layer root — used for full-board image export
  const templateLayerRef = useRef<HTMLDivElement>(null);
  const selectedLabelIdRef = useRef<string | null>(null);
  selectedLabelIdRef.current = selectedLabelId;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedLabelIds, setSelectedLabelIds] = useState<Set<string>>(new Set());
  const [selectionRect, setSelectionRect] = useState<{
    startX: number; startY: number; curX: number; curY: number;
  } | null>(null);
  const [isPanMode, setIsPanMode] = useState(false);
  const [editingId,     setEditingId]     = useState<string | null>(null);
  const [draggingId,    setDraggingId]    = useState<string | null>(null);
  const [isTransforming, setIsTransforming] = useState(false);
  const [editTriggerId,  setEditTriggerId]  = useState<string | null>(null);
  const [editHasText,    setEditHasText]    = useState(false);
  const [isResizing,     setIsResizing]     = useState(false);
  const [exportToast, setExportToast] = useState<{ message: string; severity: 'success' | 'info' | 'error'; open: boolean; duration?: number | null }>(
    { message: '', severity: 'success', open: false },
  );
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  // Tracks whether the room is currently in the Closed state. Initialised from the room's
  // persisted state so a moderator who refreshes a closed room sees "Reopen retro" immediately.
  const [isRetroLocked, setIsRetroLocked] = useState(initialRoomState === 'closed');
  // Set for non-moderators when the retroEnded broadcast fires — shows the full-screen overlay.
  const [retroEnded, setRetroEnded] = useState(false);
  const [occupancy, setOccupancy] = useState<OccupancyMap>(new Map());
  const occupancyRef = useRef<OccupancyMap>(occupancy);
  occupancyRef.current = occupancy;
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const cursorOverlayRef = useRef<CursorOverlayHandle>(null);
  const persistGenRef = useRef(0);
  const patchInFlightRef = useRef(false);
  const pendingPatchRef = useRef<CanvasSnapshot | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPanModeRef     = useRef(false);
  const selectedIdRef    = useRef<string | null>(null);
  const selectedIdsRef   = useRef<Set<string>>(new Set());
  const editingIdRef      = useRef<string | null>(null);
  const labelEditingIdRef = useRef<string | null>(null);
  const pointerDownPosRef     = useRef<{ x: number; y: number } | null>(null);
  const viewportSaveTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transformingTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizeStateRef = useRef<ResizeDragState | null>(null);
  /** Removes window-level pointerup/cancel listeners for the active resize gesture. */
  const resizeGestureCleanupRef = useRef<(() => void) | null>(null);
  const selectionRectRef = useRef<{ startX: number; startY: number; curX: number; curY: number } | null>(null);
  const multiDragStartRef      = useRef<Map<string, { x: number; y: number }>>(new Map());
  const multiLabelDragStartRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const [isGroupDragging, setIsGroupDragging] = useState(false);
  const groupDragRef = useRef<{ startClientX: number; startClientY: number } | null>(null);

  // Single write path for pan/zoom so no handler can scroll past the grid.
  const setClampedTransform = useCallback((
    x: number,
    y: number,
    scale: number,
    options?: {
      animationTime?: number;
      animationType?: 'easeOut';
      onlyIfChanged?: boolean;
      instance?: ReactZoomPanPinchRef;
    },
  ): StoredViewport | undefined => {
    const instance = options?.instance ?? transformRef.current;
    const el = containerRef.current;
    if (!instance || !el) return undefined;
    const clamped = clampPan(x, y, scale, el.clientWidth, el.clientHeight);
    if (!options?.onlyIfChanged || clamped.positionX !== x || clamped.positionY !== y) {
      instance.setTransform(
        clamped.positionX,
        clamped.positionY,
        clamped.scale,
        options?.animationTime ?? 0,
        options?.animationType,
      );
    }
    return { x: clamped.positionX, y: clamped.positionY, scale: clamped.scale };
  }, []);

  // --- Undo/redo history ---
  const history = useCanvasHistory();
  const { undo: historyUndo, redo: historyRedo, peekUndo, peekRedo, canUndo, canRedo } = history;
  const { register: registerRetroActions, unregister: unregisterRetroActions } = useRetroActionsRegistration();

  // --- Object clipboard (copy/paste/duplicate/cut) ---
  // Stored as a ref to avoid re-renders; hasClipboard drives UI state.
  const clipboardRef = useRef<{ stickies: StickyState[]; textLabels: TextLabelState[] } | null>(null);
  // Accumulating offset so repeated pastes don't stack exactly on top of each other.
  const pasteOffsetCountRef = useRef(0);
  const [hasClipboard, setHasClipboard] = useState(false);

  // Keep refs in sync for event-handler closures. During a local drag, React
  // state is stale on purpose (no setState per pointer frame) — merge live x/y
  // so a remote packet cannot reset the items this client is moving.
  const stickiesRef = useRef(stickies);
  stickiesRef.current = mergeLiveDragPositions(
    stickies,
    stickiesRef.current,
    unionIdSets(localDragStickyIdsRef.current, remoteDragStickyIdsRef.current),
  );
  textLabelsRef.current = mergeLiveDragPositions(
    textLabels,
    textLabelsRef.current,
    unionIdSets(localDragLabelIdsRef.current, remoteDragLabelIdsRef.current),
  );
  const selectedLabelIdsRef = useRef<Set<string>>(new Set());
  selectedLabelIdsRef.current = selectedLabelIds;
  // Derive the single selected id — only set when exactly one sticky is selected.
  // Used by the color strip, resize handles, and single-item keyboard shortcuts.
  const selectedId = selectedIds.size === 1 ? Array.from(selectedIds)[0] : null;
  selectedIdRef.current    = selectedId;
  selectedIdsRef.current   = selectedIds;
  editingIdRef.current      = editingId;
  labelEditingIdRef.current = labelEditingId;
  selectionRectRef.current = selectionRect;

  // Bounding box that encloses all currently selected stickies and labels.
  // Recomputed whenever stickies/labels move or the selection changes.
  const groupBounds = useMemo(() => {
    const totalSelected = selectedIds.size + selectedLabelIds.size;
    if (totalSelected < 2) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let minZ = Infinity;
    stickies.forEach(s => {
      if (!selectedIds.has(s.id)) return;
      const { w: sw, h: sh } = stickyDisplaySize(s.width, s.height, s.shape);
      minX = Math.min(minX, s.x);      minY = Math.min(minY, s.y);
      maxX = Math.max(maxX, s.x + sw); maxY = Math.max(maxY, s.y + sh);
      minZ = Math.min(minZ, itemZIndex(s));
    });
    textLabels.forEach(l => {
      if (!selectedLabelIds.has(l.id)) return;
      const el = labelRefsRef.current.get(l.id);
      const lw = el ? el.clientWidth  : 200;
      const lh = el ? el.clientHeight : 56;
      minX = Math.min(minX, l.x);      minY = Math.min(minY, l.y);
      maxX = Math.max(maxX, l.x + lw); maxY = Math.max(maxY, l.y + lh);
      minZ = Math.min(minZ, itemZIndex(l));
    });
    if (minX === Infinity) return null;
    // Sit just under the lowest selected item so stickies/labels still receive
    // pointer events while gaps inside the box initiate a group drag.
    const boxZ = Number.isFinite(minZ) ? Math.max(0, minZ - 1) : 0;
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY, zIndex: boxZ };
  }, [stickies, selectedIds, textLabels, selectedLabelIds]);

  // Drop is the only React x/y commit; clear the group-box inline left/top so
  // sx owns position again. Skip while a local drag is in progress (no setState,
  // so groupBounds is stale and must not wipe the live box).
  useLayoutEffect(() => {
    if (isGroupDragging) return;
    if (localDragStickyIdsRef.current.size > 0 || localDragLabelIdsRef.current.size > 0) return;
    clearLiveItemPosition(groupBoundsElRef.current);
  }, [groupBounds, isGroupDragging]);

  // --- SignalR sync ---

  const handleSnapshot = useCallback((incoming: CanvasSnapshot) => {
    clearLiveDragIds(remoteDragStickyIdsRef.current, remoteDragStickyIdsRef.current, stickyRefsRef.current);
    clearLiveDragIds(remoteDragLabelIdsRef.current, remoteDragLabelIdsRef.current, labelRefsRef.current);
    setStickies(incoming.stickies ?? []);
    setTextLabels(incoming.textLabels ?? []);
  }, []);

  const isHeldLocally = useCallback((kind: 'sticky' | 'label', id: string) => {
    return occupancySessionRef.current.isClaimed(kind, id);
  }, []);

  const handleStickyChanged = useCallback((diff: StickyState) => {
    if (isHeldLocally('sticky', diff.id)) return;
    setStickies(prev => mergeById(prev, diff));
  }, [isHeldLocally]);

  const handleStickyDeleted = useCallback((stickyId: string) => {
    if (isHeldLocally('sticky', stickyId)) return;
    setStickies(prev => prev.filter(s => s.id !== stickyId));
  }, [isHeldLocally]);

  const handleTextLabelChanged = useCallback((diff: TextLabelState) => {
    if (isHeldLocally('label', diff.id)) return;
    setTextLabels(prev => mergeById(prev, diff));
  }, [isHeldLocally]);

  const handleTextLabelDeleted = useCallback((labelId: string) => {
    if (isHeldLocally('label', labelId)) return;
    setTextLabels(prev => prev.filter(l => l.id !== labelId));
    setSelectedLabelId(prev => (prev === labelId ? null : prev));
  }, [isHeldLocally]);

  // Live drag from *other* clients: DOM + refs only, same as local pointermove.
  // Skip items this client is currently dragging — those positions are
  // imperative until drop and must not fight a remote packet.
  const handleItemsMoved = useCallback((items: CanvasItemMove[]) => {
    if (!items?.length) return;
    const localS = localDragStickyIdsRef.current;
    const localL = localDragLabelIdsRef.current;
    const rest = (localS.size || localL.size)
      ? items.filter(i => i.kind === 'sticky' ? !localS.has(i.id) : !localL.has(i.id))
      : items;
    const visible = rest.filter(i => !isHeldLocally(i.kind === 'label' ? 'label' : 'sticky', i.id));
    if (!visible.length) return;
    const stickyMoves = visible.filter(i => i.kind !== 'label');
    const labelMoves = visible.filter(i => i.kind === 'label');
    if (stickyMoves.length) {
      stickiesRef.current = applyRemoteLivePositions(
        stickiesRef.current, stickyMoves, stickyRefsRef.current, remoteDragStickyIdsRef.current,
      );
    }
    if (labelMoves.length) {
      textLabelsRef.current = applyRemoteLivePositions(
        textLabelsRef.current, labelMoves, labelRefsRef.current, remoteDragLabelIdsRef.current,
      );
    }
  }, [isHeldLocally]);

  // Compact multi-item live drag: one shared (dx, dy) applied to every listed id.
  const handleItemsMovedDelta = useCallback((payload: CanvasItemsMovedDelta) => {
    const { dx, dy } = payload;
    if (dx === 0 && dy === 0) return;
    const localS = localDragStickyIdsRef.current;
    const localL = localDragLabelIdsRef.current;
    const stickyIds = localS.size
      ? payload.stickyIds?.filter(id => !localS.has(id))
      : payload.stickyIds;
    const labelIds = localL.size
      ? payload.labelIds?.filter(id => !localL.has(id))
      : payload.labelIds;
    const remoteStickyIds = stickyIds?.filter(id => !isHeldLocally('sticky', id));
    const remoteLabelIds = labelIds?.filter(id => !isHeldLocally('label', id));
    if (remoteStickyIds?.length) {
      stickiesRef.current = applyRemoteLiveDelta(
        stickiesRef.current, remoteStickyIds, dx, dy, stickyRefsRef.current, remoteDragStickyIdsRef.current,
      );
    }
    if (remoteLabelIds?.length) {
      textLabelsRef.current = applyRemoteLiveDelta(
        textLabelsRef.current, remoteLabelIds, dx, dy, labelRefsRef.current, remoteDragLabelIdsRef.current,
      );
    }
  }, [isHeldLocally]);

  // Multi-item drop: React x/y was not updated during the relay — take live
  // coords from refs, stamp updatedAt, then drop the inline left/top.
  const handleItemsMoveCommitted = useCallback((payload: CanvasItemsMoveCommitted) => {
    const { stickyIds, labelIds, updatedAt } = payload;
    const remoteStickyIds = stickyIds?.filter(id => !isHeldLocally('sticky', id));
    const remoteLabelIds = labelIds?.filter(id => !isHeldLocally('label', id));
    if (remoteStickyIds?.length) {
      const idSet = new Set(remoteStickyIds);
      const liveById = new Map(stickiesRef.current.map(s => [s.id, s]));
      setStickies(prev => prev.map(s => {
        if (!idSet.has(s.id)) return s;
        const live = liveById.get(s.id);
        return { ...s, x: live?.x ?? s.x, y: live?.y ?? s.y, updatedAt };
      }));
      clearLiveDragIds(remoteStickyIds, remoteDragStickyIdsRef.current, stickyRefsRef.current);
    }
    if (remoteLabelIds?.length) {
      const idSet = new Set(remoteLabelIds);
      const liveById = new Map(textLabelsRef.current.map(l => [l.id, l]));
      setTextLabels(prev => prev.map(l => {
        if (!idSet.has(l.id)) return l;
        const live = liveById.get(l.id);
        return { ...l, x: live?.x ?? l.x, y: live?.y ?? l.y, updatedAt };
      }));
      clearLiveDragIds(remoteLabelIds, remoteDragLabelIdsRef.current, labelRefsRef.current);
    }
  }, [isHeldLocally]);

  // Drag-end (and other batched commits) last-writer-wins over any in-flight live moves.
  const handleItemsCommitted = useCallback((incoming: CanvasSnapshot) => {
    if (incoming.stickies?.length) {
      const stickies = incoming.stickies.filter(s => !isHeldLocally('sticky', s.id));
      if (stickies.length) {
        setStickies(prev => mergeAllById(prev, stickies));
        clearLiveDragIds(stickies.map(s => s.id), remoteDragStickyIdsRef.current, stickyRefsRef.current);
      }
    }
    if (incoming.textLabels?.length) {
      const labels = incoming.textLabels.filter(l => !isHeldLocally('label', l.id));
      if (labels.length) {
        setTextLabels(prev => mergeAllById(prev, labels));
        clearLiveDragIds(labels.map(l => l.id), remoteDragLabelIdsRef.current, labelRefsRef.current);
      }
    }
  }, [isHeldLocally]);

  // Compact bring-to-front: assigns zIndex = baseZ, baseZ+1, … to stickyIds, then
  // continues the sequence into labelIds — mirrors the sender's bringToFront.
  const handleItemsBroughtToFront = useCallback((payload: CanvasItemsBroughtToFront) => {
    const stickyIds = (payload.stickyIds ?? []).filter(id => !isHeldLocally('sticky', id));
    const labelIds = (payload.labelIds ?? []).filter(id => !isHeldLocally('label', id));
    if (stickyIds.length === 0 && labelIds.length === 0) return;
    if (stickyIds.length) {
      setStickies(prev => applyZIndexSequence(prev, stickyIds, payload.baseZ, payload.updatedAt));
    }
    if (labelIds.length) {
      setTextLabels(prev => applyZIndexSequence(prev, labelIds, payload.baseZ + stickyIds.length, payload.updatedAt));
    }
  }, [isHeldLocally]);

  const handleItemsDeleted = useCallback((payload: CanvasItemsDeleted) => {
    const stickyIds = (payload.stickyIds ?? []).filter(id => !isHeldLocally('sticky', id));
    const labelIds = (payload.labelIds ?? []).filter(id => !isHeldLocally('label', id));
    if (stickyIds.length) {
      const remove = new Set(stickyIds);
      setStickies(prev => prev.filter(s => !remove.has(s.id)));
      clearLiveDragIds(stickyIds, remoteDragStickyIdsRef.current, stickyRefsRef.current);
    }
    if (labelIds.length) {
      const remove = new Set(labelIds);
      setTextLabels(prev => prev.filter(l => !remove.has(l.id)));
      setSelectedLabelId(prev => (prev && remove.has(prev) ? null : prev));
      clearLiveDragIds(labelIds, remoteDragLabelIdsRef.current, labelRefsRef.current);
    }
  }, [isHeldLocally]);

  const fitTemplateViewport = useCallback((id: string) => {
    const template = getTemplate(id);
    if (!template) return;
    requestAnimationFrame(() => {
      const el = containerRef.current;
      if (!el) return;
      const bounds = templateBoardBounds(template);
      const fit = computeFitTransform(
        [{
          x: bounds.minX,
          y: bounds.minY,
          width: bounds.maxX - bounds.minX,
          height: bounds.maxY - bounds.minY,
        }],
        el.clientWidth,
        el.clientHeight,
      );
      if (fit) {
        const vp = setClampedTransform(fit.positionX, fit.positionY, fit.scale, {
          animationTime: 300,
          animationType: 'easeOut',
        });
        if (vp) saveViewport(roomUrlId, vp);
      }
    });
  }, [roomUrlId, setClampedTransform]);

  const handleTemplateChanged = useCallback((template: string | null) => {
    setTemplateId(template);
    // Fit viewport to the new template so all participants see it, matching
    // what the moderator's client does after applying via handleApplyTemplate.
    if (template) fitTemplateViewport(template);
  }, [fitTemplateViewport]);

  const fitViewport = useCallback((snapshotStickies: StickyState[]) => {
    requestAnimationFrame(() => {
      const el = containerRef.current;
      if (!el) return;

      const viewportW = el.clientWidth;
      const viewportH = el.clientHeight;

      // Fit to stickies only (labels are typically at the top edge of sticky groups)
      const fit = computeFitTransform(snapshotStickies, viewportW, viewportH);
      if (fit) {
        setClampedTransform(fit.positionX, fit.positionY, fit.scale, {
          animationTime: 300,
          animationType: 'easeOut',
        });
      } else {
        const centre = computeCentreTransform(viewportW, viewportH, CANVAS_SIZE / 2, CANVAS_SIZE / 2);
        setClampedTransform(centre.positionX, centre.positionY, centre.scale);
      }
    });
  }, [roomUrlId, setClampedTransform]);

  const handleApplyTemplate = useCallback(async (selectedId: string | null) => {
    if (!isModerator) return;
    if (selectedId && !isValidTemplateId(selectedId)) return;

    setTemplatePickerOpen(false);
    setSpeedDialOpen(false);

    try {
      await new HttpClient().patch(
        `/retro-rooms/${roomUrlId}/template`,
        new SetRetroTemplateRequest(playerId, selectedId),
      );
      setTemplateId(selectedId);
      if (selectedId) {
        fitTemplateViewport(selectedId);
        track.event(RetroCategory.Template, RetroAction.TemplateSelected, selectedId);
      } else {
        // Template cleared — fit to current stickies so the board stays in view.
        fitViewport(stickiesRef.current);
        track.event(RetroCategory.Template, RetroAction.TemplateCleared);
      }
    } catch {
      setExportToast({ message: 'Could not update template', severity: 'error', open: true });
    }
  }, [isModerator, roomUrlId, playerId, fitTemplateViewport, fitViewport, track]);

  const {
    broadcastItemsMoved,
    flushLiveMoves,
    broadcastItemsMovedDelta,
    flushItemsMovedDelta,
    resetItemsMovedDelta,
    broadcastItemsCommitted,
    broadcastItemsMoveCommitted,
    broadcastItemsBroughtToFront,
    broadcastItemsDeleted,
    broadcastCursor,
    broadcastCursorLeft,
    tryAcquireItemLocks,
    releaseItemLocks,
  } = useRetroSync({
    roomUrlId,
    playerId,
    playerName,
    onSnapshot: handleSnapshot,
    onStickyChanged: handleStickyChanged,
    onStickyDeleted: handleStickyDeleted,
    onTextLabelChanged: handleTextLabelChanged,
    onTextLabelDeleted: handleTextLabelDeleted,
    onItemsMoved: handleItemsMoved,
    onItemsMovedDelta: handleItemsMovedDelta,
    onItemsMoveCommitted: handleItemsMoveCommitted,
    onItemsCommitted: handleItemsCommitted,
    onItemsBroughtToFront: handleItemsBroughtToFront,
    onItemsDeleted: handleItemsDeleted,
    onCursorMoved: cursor => cursorOverlayRef.current?.handleMoved(cursor),
    onCursorLeft: playerId => cursorOverlayRef.current?.handleLeft(playerId),
    onItemLocksSnapshot: (locks: ItemLock[]) => {
      setOccupancy(applyLocksSnapshot(locks));
    },
    onItemLocksChanged: (changed: ItemLocksChanged) => {
      setOccupancy(prev => applyLocksChanged(prev, changed));
    },
    onPersistError: () => {
      setExportToast({
        message: 'Could not save canvas changes. Retrying…',
        severity: 'error',
        open: true,
      });
    },
    onTemplateChanged: handleTemplateChanged,
    // Moderators stay on the canvas and see a snackbar + the menu item flips to "Reopen retro".
    // Non-moderators see the full-screen overlay so they know the board is locked.
    onRetroEnded: () => {
      setIsRetroLocked(true);
      if (isModerator) {
        setExportToast({
          message: 'Retro ended — the board is now closed to new participants. Signed-in members can reopen it from their recent retros.',
          severity: 'info',
          open: true,
          duration: null,
        });
      } else {
        setRetroEnded(true);
      }
    },
    onRetroReopened: () => {
      setIsRetroLocked(false);
      setRetroEnded(false);
      if (isModerator) {
        setExportToast({
          message: 'Retro reopened — participants can join again.',
          severity: 'info',
          open: true,
          duration: 5000,
        });
      }
    },
  });

  // --- Persistence ---
  // Live drag is relay-only (ItemsMoved). Mongo is written once per gesture, only on
  // drop (pointerup/pointercancel/lostpointercapture), via ItemsCommitted / ItemsDeleted.
  // REST PATCH is hub-failure fallback only (serialized, latest snapshot wins).

  const snapshotFromRefs = useCallback((): CanvasSnapshot => ({
    stickies: stickiesRef.current.map(s => ({
      ...s,
      zIndex: s.zIndex ?? 0,
      content: clampTextLength(s.content ?? '', MAX_STICKY_CONTENT_LENGTH),
    })),
    textLabels: textLabelsRef.current.map(l => ({
      ...l,
      zIndex: l.zIndex ?? 0,
      content: clampTextLength(l.content ?? '', MAX_LABEL_CONTENT_LENGTH),
    })),
  }), []);

  const patchCanvas = useCallback(() => {
    pendingPatchRef.current = snapshotFromRefs();
    const flushPendingPatch = () => {
      if (patchInFlightRef.current) return;
      const payload = pendingPatchRef.current;
      if (!payload) return;
      pendingPatchRef.current = null;
      patchInFlightRef.current = true;
      new HttpClient()
        .patch(
          `/retro-rooms/${roomUrlId}/canvas?playerId=${encodeURIComponent(playerId)}`,
          payload as unknown as Parameters<HttpClient['patch']>[1],
        )
        .catch(console.error)
        .finally(() => {
          patchInFlightRef.current = false;
          if (pendingPatchRef.current) flushPendingPatch();
        });
    };
    flushPendingPatch();
  }, [roomUrlId, playerId, snapshotFromRefs]);

  const persistCommit = useCallback((stickies: StickyState[], labels: TextLabelState[]) => {
    const nextStickies = stickies.length > 0
      ? mergeAllById(stickiesRef.current, stickies)
      : stickiesRef.current;
    const nextLabels = labels.length > 0
      ? mergeAllById(textLabelsRef.current, labels)
      : textLabelsRef.current;
    stickiesRef.current = nextStickies;
    textLabelsRef.current = nextLabels;
    const gen = ++persistGenRef.current;
    void broadcastItemsCommitted(stickies, labels).then(ok => {
      if (!ok && persistGenRef.current === gen) patchCanvas();
    });
  }, [broadcastItemsCommitted, patchCanvas]);

  const persistDelete = useCallback((stickyIds: string[], labelIds: string[]) => {
    const removeStickies = new Set(stickyIds);
    const removeLabels = new Set(labelIds);
    stickiesRef.current = stickiesRef.current.filter(s => !removeStickies.has(s.id));
    textLabelsRef.current = textLabelsRef.current.filter(l => !removeLabels.has(l.id));
    const gen = ++persistGenRef.current;
    void broadcastItemsDeleted(stickyIds, labelIds).then(ok => {
      if (!ok && persistGenRef.current === gen) patchCanvas();
    });
  }, [broadcastItemsDeleted, patchCanvas]);

  // Undo/redo: apply the restored snapshot, then one hub generation for
  // ItemsDeleted (if any) followed by ItemsCommitted (if any).
  const persistRestore = useCallback((
    restoredStickies: StickyState[],
    restoredLabels: TextLabelState[],
    upsertStickies: StickyState[],
    upsertLabels: TextLabelState[],
    deleteStickyIds: string[],
    deleteLabelIds: string[],
  ) => {
    stickiesRef.current = restoredStickies;
    textLabelsRef.current = restoredLabels;
    const hasDeletes = deleteStickyIds.length > 0 || deleteLabelIds.length > 0;
    const hasUpserts = upsertStickies.length > 0 || upsertLabels.length > 0;
    if (!hasDeletes && !hasUpserts) return;
    const gen = ++persistGenRef.current;
    void (async () => {
      let ok = true;
      if (hasDeletes) {
        ok = (await broadcastItemsDeleted(deleteStickyIds, deleteLabelIds)) && ok;
      }
      if (hasUpserts) {
        ok = (await broadcastItemsCommitted(upsertStickies, upsertLabels)) && ok;
      }
      if (!ok && persistGenRef.current === gen) patchCanvas();
    })();
  }, [broadcastItemsCommitted, broadcastItemsDeleted, patchCanvas]);

  const anyLockedByOther = useCallback((stickyIds: string[], labelIds: string[]) => {
    const occupancyMap = occupancyRef.current;
    return stickyIds.some(id => isOccupiedByOther(occupancyMap, 'sticky', id, playerId))
      || labelIds.some(id => isOccupiedByOther(occupancyMap, 'label', id, playerId));
  }, [playerId]);

  const releaseLocks = useCallback((stickyIds: string[], labelIds: string[]) => {
    const uniqueStickies = Array.from(new Set(stickyIds.filter(Boolean)));
    const uniqueLabels = Array.from(new Set(labelIds.filter(Boolean)));
    if (uniqueStickies.length === 0 && uniqueLabels.length === 0) return;
    occupancySessionRef.current.release(uniqueStickies, uniqueLabels);
    releaseItemLocks(uniqueStickies, uniqueLabels);
  }, [releaseItemLocks]);

  const snapshotGestureOrigin = useCallback(() => {
    if (gestureOriginRef.current) return;
    gestureOriginRef.current = {
      stickies: stickiesRef.current.map(s => ({ ...s })),
      labels: textLabelsRef.current.map(l => ({ ...l })),
    };
  }, []);

  const restoreGestureOrigin = useCallback(() => {
    const origin = gestureOriginRef.current;
    gestureOriginRef.current = null;
    if (!origin) return;
    stickiesRef.current = origin.stickies;
    textLabelsRef.current = origin.labels;
    setStickies(origin.stickies);
    setTextLabels(origin.labels);
    origin.stickies.forEach(s => clearLiveItemPosition(stickyRefsRef.current.get(s.id)));
    origin.labels.forEach(l => clearLiveItemPosition(labelRefsRef.current.get(l.id)));
    clearLiveItemPosition(groupBoundsElRef.current);
    localDragStickyIdsRef.current = new Set();
    localDragLabelIdsRef.current = new Set();
    groupBoxStartRef.current = null;
    groupDragRef.current = null;
    groupMovingRef.current = false;
    setIsGroupDragging(false);
    setIsResizing(false);
    resizeStateRef.current = null;
    resizeGestureCleanupRef.current?.();
    resizeGestureCleanupRef.current = null;
  }, []);

  const persistWhenHeld = useCallback((stickyIds: string[], labelIds: string[], run: () => void) => {
    occupancySessionRef.current.persistWhenHeld(stickyIds, labelIds, run);
  }, []);

  const clearLocalSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectedLabelIds(new Set());
    setSelectedLabelId(null);
    setEditingId(null);
    setLabelEditingId(null);
    setEditTriggerId(null);
    setLabelEditTriggerId(null);
  }, []);

  const denyOccupancySession = useCallback(() => {
    const session = occupancySessionRef.current;
    session.discardQueuedPersist();
    session.invalidateInFlight();
    const heldS = Array.from(session.heldStickies);
    const heldL = Array.from(session.heldLabels);
    releaseLocks(heldS, heldL);
    restoreGestureOrigin();
    setOccupancyAbortEpoch(n => n + 1);
    clearLocalSelection();
    setExportToast({ message: OCCUPANCY_DENY_TOAST, severity: 'info', open: true });
  }, [clearLocalSelection, releaseLocks, restoreGestureOrigin]);

  /**
   * Selection is the occupancy session: acquire on select / lasso-up, release on
   * deselect. Grant is confirmed in parallel so drag/edit can start immediately.
   */
  const syncOccupancyToSelection = useCallback((stickyIds: string[], labelIds: string[]) => {
    const session = occupancySessionRef.current;
    const wantS = Array.from(new Set(stickyIds.filter(Boolean)));
    const wantL = Array.from(new Set(labelIds.filter(Boolean)));
    const diff = diffOccupancySelection(
      session.heldStickies,
      session.heldLabels,
      wantS,
      wantL,
    );

    if (diff.toReleaseStickies.length > 0 || diff.toReleaseLabels.length > 0) {
      releaseLocks(diff.toReleaseStickies, diff.toReleaseLabels);
    }

    if (wantS.length === 0 && wantL.length === 0) {
      session.invalidateInFlight();
      return;
    }

    if (diff.toAcquireStickies.length === 0 && diff.toAcquireLabels.length === 0) {
      return;
    }

    if (anyLockedByOther(wantS, wantL)) {
      denyOccupancySession();
      return;
    }

    const gen = session.beginAcquire(diff.toAcquireStickies, diff.toAcquireLabels);

    void tryAcquireItemLocks(wantS, wantL).then(result => {
      const selectedL = new Set(selectedLabelIdsRef.current);
      if (selectedLabelIdRef.current) selectedL.add(selectedLabelIdRef.current);
      const applied = session.applyAcquireResult(
        gen,
        !!result.granted,
        selectedIdsRef.current,
        selectedL,
        wantS,
        wantL,
      );
      if (!applied.granted) {
        if (applied.shouldAbortSession) denyOccupancySession();
        return;
      }
      if (applied.releaseStickies.length > 0 || applied.releaseLabels.length > 0) {
        releaseLocks(applied.releaseStickies, applied.releaseLabels);
      }
    });
  }, [anyLockedByOther, denyOccupancySession, releaseLocks, tryAcquireItemLocks]);

  const deselectAndRelease = useCallback(() => {
    clearLocalSelection();
    syncOccupancyToSelection([], []);
  }, [clearLocalSelection, syncOccupancyToSelection]);

  const handleEditingChange  = useCallback((id: string | null) => {
    setEditingId(id);
  }, []);
  const handleLabelEditingChange = useCallback((id: string | null) => {
    setLabelEditingId(id);
  }, []);

  /**
   * Raise stickies/labels to the top of the shared z stack (preserving their
   * relative order). No-ops when every target is already above all other items.
   * Does not push undo history — stacking is a side-effect of select/drag/create.
   */
  const bringToFront = useCallback((stickyIds: string[], labelIds: string[]) => {
    const occupancyMap = occupancyRef.current;
    const unlockedStickies = stickyIds.filter(id => !isOccupiedByOther(occupancyMap, 'sticky', id, playerId));
    const unlockedLabels = labelIds.filter(id => !isOccupiedByOther(occupancyMap, 'label', id, playerId));
    const stickies = stickiesRef.current;
    const labels = textLabelsRef.current;
    const order = computeBringToFrontOrder(stickies, labels, unlockedStickies, unlockedLabels);
    if (!order) return;

    const { orderedStickyIds, orderedLabelIds, baseZ } = order;
    const labelBaseZ = baseZ + orderedStickyIds.length;
    const now = new Date().toISOString();

    if (orderedStickyIds.length > 0) {
      const nextStickies = applyZIndexSequence(stickies, orderedStickyIds, baseZ, now);
      stickiesRef.current = nextStickies;
      setStickies(nextStickies);
    }
    if (orderedLabelIds.length > 0) {
      const nextLabels = applyZIndexSequence(labels, orderedLabelIds, labelBaseZ, now);
      textLabelsRef.current = nextLabels;
      setTextLabels(nextLabels);
    }

    const gen = ++persistGenRef.current;
    void broadcastItemsBroughtToFront(orderedStickyIds, orderedLabelIds, baseZ, now).then(ok => {
      if (!ok && persistGenRef.current === gen) patchCanvas();
    });
  }, [broadcastItemsBroughtToFront, patchCanvas, playerId]);

  const endLocalDrag = useCallback(() => {
    localDragStickyIdsRef.current = new Set();
    localDragLabelIdsRef.current = new Set();
    groupBoxStartRef.current = null;
  }, []);

  /** Snapshot positions of all selected items (stickies + labels) into the drag-start maps. */
  const populateDragMaps = useCallback(() => {
    const stickyMap = new Map<string, { x: number; y: number }>();
    stickiesRef.current.forEach(s => {
      if (selectedIdsRef.current.has(s.id)) stickyMap.set(s.id, { x: s.x, y: s.y });
    });
    multiDragStartRef.current = stickyMap;
    const labelMap = new Map<string, { x: number; y: number }>();
    textLabelsRef.current.forEach(l => {
      if (selectedLabelIdsRef.current.has(l.id)) labelMap.set(l.id, { x: l.x, y: l.y });
    });
    multiLabelDragStartRef.current = labelMap;
    localDragStickyIdsRef.current = new Set(stickyMap.keys());
    localDragLabelIdsRef.current = new Set(labelMap.keys());
    const box = groupBoundsElRef.current;
    groupBoxStartRef.current = box ? { x: box.offsetLeft, y: box.offsetTop } : null;
    // A new multi-drag gesture is starting — the relay's cumulative-to-incremental
    // conversion must start counting from zero again.
    resetItemsMovedDelta();
  }, [resetItemsMovedDelta]);

  const handleLabelDraggingChange = useCallback((id: string | null) => {
    if (id) {
      snapshotGestureOrigin();
      history.pushHistory(stickiesRef.current, textLabelsRef.current);
      const totalSelected = selectedIdsRef.current.size + selectedLabelIdsRef.current.size;
      if (selectedLabelIdsRef.current.has(id) && totalSelected > 1) {
        bringToFront(
          Array.from(selectedIdsRef.current),
          Array.from(selectedLabelIdsRef.current),
        );
      } else {
        bringToFront([], [id]);
      }
    }
    setLabelDraggingId(id);
    // Only touch the multi-drag maps when a drag starts. On drag end (id === null)
    // onDraggingChange(null) fires *before* onMultiDragEnd, so clearing here would wipe
    // the start positions onMultiDragEnd still needs to compute and persist the final
    // move — it clears them itself once done.
    if (!id) return;
    const totalSelected = selectedIdsRef.current.size + selectedLabelIdsRef.current.size;
    if (selectedLabelIdsRef.current.has(id) && totalSelected > 1) {
      populateDragMaps();
    } else {
      multiDragStartRef.current = new Map();
      multiLabelDragStartRef.current = new Map();
      localDragStickyIdsRef.current = new Set();
      localDragLabelIdsRef.current = new Set([id]);
      groupBoxStartRef.current = null;
    }
  }, [bringToFront, history, populateDragMaps, snapshotGestureOrigin]);

  const handleDraggingChange = useCallback((id: string | null) => {
    if (id) {
      // Snapshot canvas state before any drag-induced position changes so the
      // drag can be undone as a single action.
      snapshotGestureOrigin();
      history.pushHistory(stickiesRef.current, textLabelsRef.current);
      const totalSelected = selectedIdsRef.current.size + selectedLabelIdsRef.current.size;
      if (selectedIdsRef.current.has(id) && totalSelected > 1) {
        bringToFront(
          Array.from(selectedIdsRef.current),
          Array.from(selectedLabelIdsRef.current),
        );
      } else {
        bringToFront([id], []);
      }
    }
    setDraggingId(id);
    // Only touch the multi-drag maps when a drag starts. On drag end (id === null)
    // onDraggingChange(null) fires *before* onMultiDragEnd, so clearing here would wipe
    // the start positions onMultiDragEnd still needs to compute and persist the final
    // move — it clears them itself once done.
    if (!id) return;
    const totalSelected = selectedIdsRef.current.size + selectedLabelIdsRef.current.size;
    if (selectedIdsRef.current.has(id) && totalSelected > 1) {
      populateDragMaps();
    } else {
      multiDragStartRef.current = new Map();
      multiLabelDragStartRef.current = new Map();
      localDragStickyIdsRef.current = new Set([id]);
      localDragLabelIdsRef.current = new Set();
      groupBoxStartRef.current = null;
    }
  }, [bringToFront, history, populateDragMaps, snapshotGestureOrigin]);
  const handleHasTextChange  = useCallback((_id: string, hasText: boolean) => {
    setEditHasText(hasText);
  }, []);
  const handleLabelHasTextChange = useCallback((_id: string, hasText: boolean) => {
    setLabelEditHasText(hasText);
  }, []);

  // Debounced viewport save — shared by the wheel handler and panning callbacks
  const debounceSave = useCallback((vp: StoredViewport) => {
    if (viewportSaveTimerRef.current) clearTimeout(viewportSaveTimerRef.current);
    viewportSaveTimerRef.current = setTimeout(() => saveViewport(roomUrlId, vp), VIEWPORT_SAVE_DEBOUNCE_MS);
  }, [roomUrlId]);

  // Marks an in-progress transform (pan/zoom) so overlays that live outside
  // TransformComponent (color strips) can hide themselves to avoid lag.
  const markTransforming = useCallback(() => {
    setIsTransforming(true);
    if (transformingTimerRef.current) clearTimeout(transformingTimerRef.current);
    transformingTimerRef.current = setTimeout(() => setIsTransforming(false), 300);
  }, []);

  // --- Export helpers ---

  /** Build a labelId → {w, h} map from the DOM refs measured at export time. */
  const getLabelDims = useCallback((): LabelDimensions => {
    const dims: LabelDimensions = new Map();
    labelRefsRef.current.forEach((el, id) => {
      dims.set(id, { w: el.clientWidth, h: el.clientHeight });
    });
    return dims;
  }, []);

  /**
   * Copy the canvas to the clipboard.
   * @param format  - 'markdown' or 'text'
   * @param stickiesToExport - the subset to include (all stickies or just selected)
   */
  const handleExport = useCallback(async (
    format: 'markdown' | 'text',
    stickiesToExport: StickyState[],
    scope: 'all' | 'selection',
  ): Promise<void> => {
    const dims = getLabelDims();
    const sections = groupForExport(stickiesToExport, textLabelsRef.current, templateIdRef.current, getTemplate, dims);
    const text = format === 'markdown'
      ? renderSectionsAsMarkdown(sections)
      : renderSectionsAsPlainText(sections);

    if (!text.trim()) {
      setExportToast({ message: 'No notes to copy', severity: 'info', open: true });
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      const count = countExportItems(sections);
      const formatName = format === 'markdown' ? 'Markdown' : 'plain text';
      const sectionHint = sections.length > 1 ? `, ${sections.length} sections` : '';
      setExportToast({
        message: `Copied ${count} note${count !== 1 ? 's' : ''} as ${formatName}${sectionHint}`,
        severity: 'success',
        open: true,
      });
      track.event(RetroCategory.Export, RetroAction.ExportUsed, `${format}:${scope}`);
    } catch {
      setExportToast({ message: 'Could not access clipboard', severity: 'error', open: true });
    }
  }, [getLabelDims, track]);

  /** Export only the currently selected stickies (or all if nothing is selected). */
  const handleExportAutoScope = useCallback(async (format: 'markdown' | 'text'): Promise<void> => {
    const hasSelection = selectedIdsRef.current.size > 0;
    const scope = hasSelection
      ? stickiesRef.current.filter(s => selectedIdsRef.current.has(s.id))
      : stickiesRef.current;
    await handleExport(format, scope, hasSelection ? 'selection' : 'all');
  }, [handleExport]);

  /** Export every sticky note on the board, regardless of selection. */
  const handleExportAll = useCallback(async (format: 'markdown' | 'text'): Promise<void> => {
    await handleExport(format, stickiesRef.current, 'all');
  }, [handleExport]);

  /** Export only the stickies that are currently selected. */
  const handleExportSelection = useCallback(async (format: 'markdown' | 'text'): Promise<void> => {
    const selected = stickiesRef.current.filter(s => selectedIdsRef.current.has(s.id));
    await handleExport(format, selected, 'selection');
  }, [handleExport]);

  /** Download the full board (all stickies and labels) as a transparent PNG. */
  const handleExportBoardAsImage = useCallback(async (): Promise<void> => {
    const tid = templateIdRef.current;
    const template = tid ? getTemplate(tid) : null;
    const templateInfo: TemplateExportInfo | undefined =
      template && templateLayerRef.current
        ? { el: templateLayerRef.current, boardRect: templateBoardBounds(template), isDarkMode }
        : undefined;

    try {
      await exportItemsAsImage(
        stickiesRef.current,
        textLabelsRef.current,
        stickyRefsRef.current,
        labelRefsRef.current,
        buildExportFilename('retro-board'),
        templateInfo,
      );
      track.event(RetroCategory.Export, RetroAction.ExportUsed, 'image:board');
    } catch (err) {
      console.error('Board image export failed', err);
      setExportToast({ message: 'Could not export board as image', severity: 'error', open: true });
    }
  }, [isDarkMode, track]);

  /** Download the currently selected items as a transparent PNG. */
  const handleExportSelectionAsImage = useCallback(async (): Promise<void> => {
    const selectedStickies = stickiesRef.current.filter(s => selectedIdsRef.current.has(s.id));
    const labelIdSet = new Set(selectedLabelIdsRef.current);
    if (selectedLabelIdRef.current) labelIdSet.add(selectedLabelIdRef.current);
    const selectedLabels = textLabelsRef.current.filter(l => labelIdSet.has(l.id));

    try {
      await exportItemsAsImage(
        selectedStickies,
        selectedLabels,
        stickyRefsRef.current,
        labelRefsRef.current,
        buildExportFilename('retro-selection'),
      );
      track.event(RetroCategory.Export, RetroAction.ExportUsed, 'image:selection');
    } catch (err) {
      console.error('Selection image export failed', err);
      setExportToast({ message: 'Could not export selection as image', severity: 'error', open: true });
    }
  }, [track]);

  // --- Miro-style trackpad navigation ---
  // Two-finger scroll  → pan  (WheelEvent, ctrlKey = false)
  // Pinch / Ctrl+scroll → zoom centred on cursor (WheelEvent, ctrlKey = true)
  // The native listener with { passive: false } is required to call preventDefault
  // and suppress the browser's own scroll/pinch-zoom behaviour.

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      markTransforming();

      const ref = transformRef.current;
      if (!ref) return;
      const { positionX, positionY, scale } = ref.state;

      if (e.ctrlKey) {
        // Pinch gesture or Ctrl+scroll — zoom around the pointer position
        const rect = el.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;

        const factor = computeZoomFactor(e.deltaY, e.deltaMode);
        const newScale = Math.min(Math.max(scale * factor, 0.1), 4);
        const ratio = newScale / scale;

        const newX = cursorX - (cursorX - positionX) * ratio;
        const newY = cursorY - (cursorY - positionY) * ratio;
        const vp = setClampedTransform(newX, newY, newScale);
        if (vp) debounceSave(vp);
      } else {
        // Two-finger scroll — translate the canvas
        const vp = setClampedTransform(positionX - e.deltaX, positionY - e.deltaY, scale);
        if (vp) debounceSave(vp);
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [debounceSave, setClampedTransform]);

  // --- Middle mouse button drag pan (Windows / mouse users) ---
  // Pointer button 1 = middle/scroll-wheel button.
  // We track the drag ourselves and call setTransform each frame,
  // matching the Miro / Figma convention for mouse-only environments.

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let lastX = 0;
    let lastY = 0;
    let panning = false;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 1) return;
      e.preventDefault();
      panning = true;
      lastX = e.clientX;
      lastY = e.clientY;
      el.setPointerCapture(e.pointerId);
      el.style.cursor = 'grabbing';
      markTransforming(); // hide color strips while middle-mouse panning
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!panning) return;
      const ref = transformRef.current;
      if (!ref) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      const { positionX, positionY, scale } = ref.state;
      const vp = setClampedTransform(positionX + dx, positionY + dy, scale);
      if (vp) debounceSave(vp);
    };

    const onPointerUp = () => {
      if (!panning) return;
      panning = false;
      el.style.cursor = '';
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, [debounceSave, setClampedTransform]);

  // --- Space+drag pan mode ---

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat || isPanModeRef.current) return;
      const target = e.target as HTMLElement;
      if (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      e.preventDefault();
      isPanModeRef.current = true;
      setIsPanMode(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      isPanModeRef.current = false;
      setIsPanMode(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Reset editTriggerId after the target sticky has had time to react
  useEffect(() => {
    if (!editTriggerId) return;
    const timer = setTimeout(() => setEditTriggerId(null), 200);
    return () => clearTimeout(timer);
  }, [editTriggerId]);

  // Reset labelEditTriggerId after the target label has had time to react
  useEffect(() => {
    if (!labelEditTriggerId) return;
    const timer = setTimeout(() => setLabelEditTriggerId(null), 200);
    return () => clearTimeout(timer);
  }, [labelEditTriggerId]);

  // Keep SpeedDial open after click until the user clicks an action, the FAB, or outside it.
  // Capture phase so sticky/label stopPropagation cannot keep the menu open.
  useEffect(() => {
    if (!speedDialOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const root = speedDialRef.current;
      if (root && !root.contains(event.target as Node)) {
        setSpeedDialOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [speedDialOpen]);

  // --- Initial viewport ---
  // Called once by TransformWrapper's onInit after the library has set up.
  // Uses requestAnimationFrame so clientWidth/clientHeight are final layout values.

  // --- Sticky CRUD ---

  const addSticky = useCallback((boardX: number, boardY: number): string => {
    if (stickiesRef.current.length >= MAX_STICKIES) {
      setExportToast({ message: 'This board is full', severity: 'error', open: true });
      return '';
    }
    history.pushHistory(stickiesRef.current, textLabelsRef.current);
    const now = new Date().toISOString();
    const newSticky: StickyState = {
      id: uuidv4(),
      x: boardX - DEFAULT_STICKY_SIZE / 2,
      y: boardY - DEFAULT_STICKY_SIZE / 2,
      width: DEFAULT_STICKY_SIZE,
      height: DEFAULT_STICKY_SIZE,
      content: '',
      colorSlot: 0,
      zIndex: nextZIndex(stickiesRef.current, textLabelsRef.current),
      shape: 'square',
      authorId: playerId,
      authorName: playerName,
      createdAt: now,
      updatedAt: now,
    };

    track.event(RetroCategory.Canvas, RetroAction.StickyCreated);
    setSelectedLabelId(null);
    setStickies(prev => {
      const next = [...prev, newSticky];
      stickiesRef.current = next;
      return next;
    });
    persistCommit([newSticky], []);
    setSelectedIds(new Set([newSticky.id]));
    syncOccupancyToSelection([newSticky.id], []);
    return newSticky.id;
  }, [history, playerId, playerName, persistCommit, syncOccupancyToSelection, track]);

  // --- Text label CRUD ---

  const addTextLabel = useCallback((boardX: number, boardY: number): string => {
    if (textLabelsRef.current.length >= MAX_LABELS) {
      setExportToast({ message: 'This board is full', severity: 'error', open: true });
      return '';
    }
    history.pushHistory(stickiesRef.current, textLabelsRef.current);
    const now = new Date().toISOString();
    const newLabel: TextLabelState = {
      id: uuidv4(),
      x: boardX,
      y: boardY,
      width: 0,   // unused — element is width: fit-content
      height: 0,  // unused — element is height: fit-content
      content: '',
      colorSlot: 0,
      zIndex: nextZIndex(stickiesRef.current, textLabelsRef.current),
      fontSize: DEFAULT_LABEL_FONT,
      authorId: playerId,
      authorName: playerName,
      createdAt: now,
      updatedAt: now,
    };

    track.event(RetroCategory.Canvas, RetroAction.TextLabelCreated);
    setSelectedIds(new Set());
    setTextLabels(prev => {
      const next = [...prev, newLabel];
      textLabelsRef.current = next;
      return next;
    });
    persistCommit([], [newLabel]);
    setSelectedLabelId(newLabel.id);
    syncOccupancyToSelection([], [newLabel.id]);
    return newLabel.id;
  }, [history, playerId, playerName, persistCommit, syncOccupancyToSelection, track]);

  const handleLabelChange = useCallback(
    (diff: Partial<TextLabelState> & { id: string }) => {
      // Position-only diffs are live drag: paint + relay, no React state.
      // setTextLabels here would re-render every label each pointer frame
      // (see liveDragPosition.ts). History/persist run on handleLabelDragEnd.
      const isPositionOnly = ('x' in diff || 'y' in diff) &&
        !('colorSlot' in diff) && !('fontSize' in diff) && !('content' in diff);
      if (!isPositionOnly && anyLockedByOther([], [diff.id])) return;
      if (!isPositionOnly && !occupancySessionRef.current.claimed([], [diff.id])) return;
      if (!isPositionOnly) {
        history.pushHistory(stickiesRef.current, textLabelsRef.current);
      }
      const prev = textLabelsRef.current;
      const idx = prev.findIndex(l => l.id === diff.id);
      if (idx === -1) return;
      const updated = isPositionOnly
        ? { ...prev[idx], ...diff }
        : { ...prev[idx], ...diff, updatedAt: new Date().toISOString() };
      const next = [...prev];
      next[idx] = updated;
      textLabelsRef.current = next;
      if (isPositionOnly) {
        applyLiveItemPosition(labelRefsRef.current.get(updated.id), updated.x, updated.y);
        broadcastItemsMoved([{ id: updated.id, kind: 'label', x: updated.x, y: updated.y }]);
      } else {
        setTextLabels(next);
        persistWhenHeld([], [updated.id], () => persistCommit([], [updated]));
      }
    },
    [anyLockedByOther, history, broadcastItemsMoved, persistCommit, persistWhenHeld],
  );

  const handleLabelDragEnd = useCallback(
    (id: string, x: number, y: number) => {
      if (!occupancySessionRef.current.claimed([], [id])) {
        endLocalDrag();
        return;
      }
      // First React x/y commit of the gesture — persist once on drop.
      const prev = textLabelsRef.current;
      const idx = prev.findIndex(l => l.id === id);
      if (idx === -1) {
        endLocalDrag();
        return;
      }
      const updated = { ...prev[idx], x, y, updatedAt: new Date().toISOString() };
      const next = [...prev];
      next[idx] = updated;
      textLabelsRef.current = next;
      setTextLabels(next);
      endLocalDrag();
      flushLiveMoves();
      persistWhenHeld([], [id], () => {
        persistCommit([], [updated]);
        gestureOriginRef.current = null;
      });
    },
    [endLocalDrag, flushLiveMoves, persistCommit, persistWhenHeld],
  );

  const handleLabelDelete = useCallback(
    (id: string) => {
      if (anyLockedByOther([], [id])) return;
      if (!occupancySessionRef.current.claimed([], [id])) return;
      snapshotGestureOrigin();
      history.pushHistory(stickiesRef.current, textLabelsRef.current);
      setTextLabels(prev => prev.filter(l => l.id !== id));
      persistWhenHeld([], [id], () => {
        persistDelete([], [id]);
        gestureOriginRef.current = null;
      });
      deselectAndRelease();
      track.event(RetroCategory.Canvas, RetroAction.TextLabelDeleted);
    },
    [anyLockedByOther, deselectAndRelease, history, persistDelete, persistWhenHeld, snapshotGestureOrigin, track],
  );

  const resizeLabel = useCallback(
    (id: string, delta: number) => {
      const label = textLabelsRef.current.find(l => l.id === id);
      if (!label) return;
      const current    = label.fontSize ?? DEFAULT_LABEL_FONT;
      const newFont    = Math.min(MAX_LABEL_FONT, Math.max(MIN_LABEL_FONT, current + delta));
      if (newFont === current) return;
      handleLabelChange({ id, fontSize: newFont });
    },
    [handleLabelChange],
  );

  /**
   * Ends a corner-drag resize. Idempotent so pointerup / pointercancel can both
   * call it safely. Window-level listeners are used because at min/max the handle
   * stops moving under a held pointer and the browser may cancel the gesture
   * (common with tap-and-hold) without a local pointerup — leaving isResizing
   * stuck and the color strip hidden.
   */
  const clearResizeGestureListeners = useCallback(() => {
    resizeGestureCleanupRef.current?.();
    resizeGestureCleanupRef.current = null;
  }, []);

  const finishStickyResize = useCallback(() => {
    clearResizeGestureListeners();
    const rs = resizeStateRef.current;
    resizeStateRef.current = null;
    setIsResizing(false);
    if (!rs) return;
    const finalSticky = stickiesRef.current.find(st => st.id === rs.stickyId);
    if (!finalSticky) return;
    persistWhenHeld([rs.stickyId], [], () => {
      persistCommit([{ ...finalSticky, updatedAt: new Date().toISOString() }], []);
      gestureOriginRef.current = null;
    });
    track.event(RetroCategory.Canvas, RetroAction.StickyResized, 'drag');
  }, [clearResizeGestureListeners, persistCommit, persistWhenHeld, track]);

  const finishLabelResize = useCallback(() => {
    clearResizeGestureListeners();
    const rs = resizeStateRef.current;
    resizeStateRef.current = null;
    setIsResizing(false);
    if (!rs) return;
    const finalLabel = textLabelsRef.current.find(l => l.id === rs.stickyId);
    if (!finalLabel) return;
    persistWhenHeld([], [rs.stickyId], () => {
      persistCommit([], [{ ...finalLabel, updatedAt: new Date().toISOString() }]);
      gestureOriginRef.current = null;
    });
  }, [clearResizeGestureListeners, persistCommit, persistWhenHeld]);

  const bindResizeGestureEnd = useCallback((pointerId: number, finish: () => void) => {
    clearResizeGestureListeners();
    const onEnd = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      finish();
    };
    window.addEventListener('pointerup', onEnd, true);
    window.addEventListener('pointercancel', onEnd, true);
    resizeGestureCleanupRef.current = () => {
      window.removeEventListener('pointerup', onEnd, true);
      window.removeEventListener('pointercancel', onEnd, true);
    };
  }, [clearResizeGestureListeners]);

  /** Shared pointer-down bootstrap for sticky and label corner-drag. */
  const beginResizeGesture = useCallback((
    e: React.PointerEvent<HTMLDivElement>,
    drag: ResizeDragState,
    finish: () => void,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    snapshotGestureOrigin();
    history.pushHistory(stickiesRef.current, textLabelsRef.current);
    setIsResizing(true);
    resizeStateRef.current = drag;
    bindResizeGestureEnd(e.pointerId, finish);
  }, [bindResizeGestureEnd, history, snapshotGestureOrigin]);

  /**
   * Builds ItemResizeHandleProps for a sticky or label. Keeps the map JSX thin —
   * only start-state and move-apply differ between the two item types.
   */
  const makeResizeHandleProps = useCallback((
    enabled: boolean,
    finish: () => void,
    buildDrag: (corner: ResizeCorner, e: React.PointerEvent<HTMLDivElement>) => ResizeDragState,
    applyMove: (rs: ResizeDragState, clientX: number, clientY: number) => void,
  ): ItemResizeHandleProps => {
    if (!enabled) return { showResizeHandles: false };
    return {
      showResizeHandles: true,
      onResizePointerDown: (corner, e) => beginResizeGesture(e, buildDrag(corner, e), finish),
      onResizePointerMove: (e) => {
        const rs = resizeStateRef.current;
        if (!rs) return;
        applyMove(rs, e.clientX, e.clientY);
      },
      onResizePointerUp: finish,
      onResizePointerCancel: finish,
      onResizeLostPointerCapture: finish,
    };
  }, [beginResizeGesture]);

  // Drop window resize listeners if the canvas unmounts mid-drag.
  useEffect(() => () => {
    resizeGestureCleanupRef.current?.();
    resizeGestureCleanupRef.current = null;
    resizeStateRef.current = null;
  }, []);

  const handleStickyChange = useCallback(
    (diff: Partial<StickyState> & { id: string }) => {
      // Position-only diffs are live drag: paint + relay, no React state.
      // setStickies here would re-render every sticky on the board each pointer
      // frame (see liveDragPosition.ts). History/persist run on handleDragEnd.
      const isPositionOnly = ('x' in diff || 'y' in diff) &&
        !('colorSlot' in diff) && !('width' in diff) && !('height' in diff) && !('content' in diff);
      if (!isPositionOnly && anyLockedByOther([diff.id], [])) return;
      if (!isPositionOnly && !occupancySessionRef.current.claimed([diff.id], [])) return;
      if (!isPositionOnly) {
        history.pushHistory(stickiesRef.current, textLabelsRef.current);
      }
      const prev = stickiesRef.current;
      const idx = prev.findIndex(s => s.id === diff.id);
      if (idx === -1) return;
      const updated = isPositionOnly
        ? { ...prev[idx], ...diff }
        : { ...prev[idx], ...diff, updatedAt: new Date().toISOString() };
      const next = [...prev];
      next[idx] = updated;
      stickiesRef.current = next;
      if (isPositionOnly) {
        applyLiveItemPosition(stickyRefsRef.current.get(updated.id), updated.x, updated.y);
        broadcastItemsMoved([{ id: updated.id, kind: 'sticky', x: updated.x, y: updated.y }]);
      } else {
        setStickies(next);
        persistWhenHeld([updated.id], [], () => persistCommit([updated], []));
      }
    },
    [anyLockedByOther, history, broadcastItemsMoved, persistCommit, persistWhenHeld],
  );

  const handleDragEnd = useCallback(
    (id: string, x: number, y: number) => {
      if (!occupancySessionRef.current.claimed([id], [])) {
        endLocalDrag();
        return;
      }
      // First React x/y commit of the gesture — persist once on drop.
      const prev = stickiesRef.current;
      const idx = prev.findIndex(s => s.id === id);
      if (idx === -1) {
        endLocalDrag();
        return;
      }
      const updated = { ...prev[idx], x, y, updatedAt: new Date().toISOString() };
      const next = [...prev];
      next[idx] = updated;
      stickiesRef.current = next;
      setStickies(next);
      endLocalDrag();
      flushLiveMoves();
      persistWhenHeld([id], [], () => {
        persistCommit([updated], []);
        gestureOriginRef.current = null;
      });
    },
    [endLocalDrag, flushLiveMoves, persistCommit, persistWhenHeld],
  );

  const handleDelete = useCallback(
    (id: string) => {
      if (anyLockedByOther([id], [])) return;
      if (!occupancySessionRef.current.claimed([id], [])) return;
      snapshotGestureOrigin();
      history.pushHistory(stickiesRef.current, textLabelsRef.current);
      setStickies(prev => prev.filter(s => s.id !== id));
      persistWhenHeld([id], [], () => {
        persistDelete([id], []);
        gestureOriginRef.current = null;
      });
      deselectAndRelease();
      track.event(RetroCategory.Canvas, RetroAction.StickyDeleted);
    },
    [anyLockedByOther, deselectAndRelease, history, persistDelete, persistWhenHeld, snapshotGestureOrigin, track],
  );

  const resizeSticky = useCallback(
    (id: string, delta: number) => {
      const sticky = stickiesRef.current.find(s => s.id === id);
      if (!sticky) return;
      const isSquare = sticky.shape !== 'rectangle';
      const currentSize = isSquare ? Math.min(sticky.width, sticky.height) : sticky.width;
      const newSize = Math.min(MAX_STICKY_SIZE, Math.max(MIN_STICKY_SIZE, currentSize + delta));
      if (newSize === currentSize) return;
      handleStickyChange({ id, width: newSize, height: newSize });
      track.event(RetroCategory.Canvas, RetroAction.StickyResized, 'keyboard');
    },
    [handleStickyChange, track],
  );

  const handleMultiDragMove = useCallback(
    (_id: string, dx: number, dy: number) => {
      // Same contract as single-item drag: DOM + refs + SignalR, no React
      // state. Updating the board arrays here would rebuild every item each frame.
      const startMap      = multiDragStartRef.current;
      const labelStartMap = multiLabelDragStartRef.current;
      if (startMap.size === 0 && labelStartMap.size === 0) return;
      if (startMap.size > 0) {
        const moved: { id: string; x: number; y: number }[] = [];
        const nextStickies = stickiesRef.current.map(s => {
          const start = startMap.get(s.id);
          if (!start) return s;
          const x = start.x + dx;
          const y = start.y + dy;
          moved.push({ id: s.id, x, y });
          return { ...s, x, y };
        });
        stickiesRef.current = nextStickies;
        applyLiveItemPositions(stickyRefsRef.current, moved);
      }
      if (labelStartMap.size > 0) {
        const moved: { id: string; x: number; y: number }[] = [];
        const nextLabels = textLabelsRef.current.map(l => {
          const start = labelStartMap.get(l.id);
          if (!start) return l;
          const x = start.x + dx;
          const y = start.y + dy;
          moved.push({ id: l.id, x, y });
          return { ...l, x, y };
        });
        textLabelsRef.current = nextLabels;
        applyLiveItemPositions(labelRefsRef.current, moved);
      }
      const boxStart = groupBoxStartRef.current;
      if (boxStart) {
        applyLiveItemPosition(groupBoundsElRef.current, boxStart.x + dx, boxStart.y + dy);
      }
      // dx/dy are cumulative since the gesture started (same as the local paint above);
      // broadcastItemsMovedDelta converts to the increment-since-last-send internally.
      broadcastItemsMovedDelta(Array.from(startMap.keys()), Array.from(labelStartMap.keys()), dx, dy);
    },
    [broadcastItemsMovedDelta],
  );

  const handleMultiDragEnd = useCallback(
    (_id: string, dx: number, dy: number) => {
      const startMap      = multiDragStartRef.current;
      const labelStartMap = multiLabelDragStartRef.current;
      if (startMap.size === 0 && labelStartMap.size === 0) {
        endLocalDrag();
        return;
      }
      const stickyIds = Array.from(startMap.keys());
      const labelIds  = Array.from(labelStartMap.keys());
      if (!occupancySessionRef.current.claimed(stickyIds, labelIds)) {
        endLocalDrag();
        return;
      }
      const now = new Date().toISOString();
      const newStickies = stickiesRef.current.map(s => {
        const start = startMap.get(s.id);
        return start ? { ...s, x: start.x + dx, y: start.y + dy, updatedAt: now } : s;
      });
      const newLabels = textLabelsRef.current.map(l => {
        const start = labelStartMap.get(l.id);
        return start ? { ...l, x: start.x + dx, y: start.y + dy, updatedAt: now } : l;
      });
      setStickies(newStickies);
      setTextLabels(newLabels);
      stickiesRef.current = newStickies;
      textLabelsRef.current = newLabels;
      endLocalDrag();
      flushItemsMovedDelta();

      persistWhenHeld(stickyIds, labelIds, () => {
        const gen = ++persistGenRef.current;
        void broadcastItemsMoveCommitted(stickyIds, labelIds, dx, dy, now).then(ok => {
          if (!ok && persistGenRef.current === gen) patchCanvas();
        });
        gestureOriginRef.current = null;
      });

      multiDragStartRef.current = new Map();
      multiLabelDragStartRef.current = new Map();
    },
    [endLocalDrag, flushItemsMovedDelta, broadcastItemsMoveCommitted, patchCanvas, persistWhenHeld],
  );

  // --- Group bounding box drag ---
  // Handles the case where the user drags from the gap area inside the bounding box
  // (i.e. not directly on a sticky). Start positions are populated here rather than
  // via handleDraggingChange because no StickyNote is involved.

  const handleGroupPointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    groupMovingRef.current = false;
    groupDragRef.current = { startClientX: e.clientX, startClientY: e.clientY };
    setIsGroupDragging(true);
  }, []);

  const handleGroupPointerMove = useCallback((e: React.PointerEvent) => {
    if (!groupDragRef.current) return;
    const scale = transformRef.current?.state.scale ?? 1;
    const screenDx = e.clientX - groupDragRef.current.startClientX;
    const screenDy = e.clientY - groupDragRef.current.startClientY;
    if (!groupMovingRef.current) {
      if (Math.hypot(screenDx, screenDy) <= POINTER_DRAG_THRESHOLD_PX) return;
      groupMovingRef.current = true;
      snapshotGestureOrigin();
      history.pushHistory(stickiesRef.current, textLabelsRef.current);
      bringToFront(
        Array.from(selectedIdsRef.current),
        Array.from(selectedLabelIdsRef.current),
      );
      populateDragMaps();
    }
    const dx = screenDx / scale;
    const dy = screenDy / scale;
    handleMultiDragMove('__group__', dx, dy);
  }, [bringToFront, handleMultiDragMove, history, populateDragMaps, snapshotGestureOrigin]);

  const handleGroupPointerUp = useCallback((e: React.PointerEvent) => {
    if (!groupDragRef.current) return;
    const scale = transformRef.current?.state.scale ?? 1;
    const dx = (e.clientX - groupDragRef.current.startClientX) / scale;
    const dy = (e.clientY - groupDragRef.current.startClientY) / scale;
    const moved = groupMovingRef.current;
    groupDragRef.current = null;
    groupMovingRef.current = false;
    setIsGroupDragging(false);
    if (moved) handleMultiDragEnd('__group__', dx, dy);
  }, [handleMultiDragEnd]);

  // --- Undo / Redo ---
  // Uses the Memento pattern via useCanvasHistory (see hooks/useCanvasHistory.ts).
  // pushHistory() is called BEFORE every mutation so the pre-change snapshot is
  // stored.  handleUndo/handleRedo retrieve a snapshot and apply it as new state.

  /**
   * Broadcast the minimal diff between a restored canvas state and the state that
   * was active before the restore.
   *
   * - Items that are new or whose persistable fields changed are upserted.
   * - Items that existed before the restore but are absent from it are deleted.
   * - Unchanged items are skipped. Upserts get a fresh updatedAt so the restore
   *   is a real commit even when live drag never stamped a timestamp.
   */
  const broadcastCanvasRestore = useCallback((
    restoredStickies: StickyState[],
    restoredLabels:   TextLabelState[],
    prevStickies:     StickyState[],
    prevLabels:       TextLabelState[],
  ): { stickies: StickyState[]; textLabels: TextLabelState[] } => {
    const { upsertStickies, upsertLabels, deleteStickyIds, deleteLabelIds } =
      diffCanvasRestore(restoredStickies, restoredLabels, prevStickies, prevLabels);

    const now = new Date().toISOString();
    const stampedStickies = upsertStickies.map(s => ({ ...s, updatedAt: now }));
    const stampedLabels = upsertLabels.map(l => ({ ...l, updatedAt: now }));
    const byStickyId = new Map(stampedStickies.map(s => [s.id, s]));
    const byLabelId = new Map(stampedLabels.map(l => [l.id, l]));
    const nextStickies = restoredStickies.map(s => byStickyId.get(s.id) ?? s);
    const nextLabels = restoredLabels.map(l => byLabelId.get(l.id) ?? l);

    persistRestore(
      nextStickies,
      nextLabels,
      stampedStickies,
      stampedLabels,
      deleteStickyIds,
      deleteLabelIds,
    );
    return { stickies: nextStickies, textLabels: nextLabels };
  }, [persistRestore]);

  const handleUndo = useCallback(() => {
    if (editingIdRef.current || labelEditingIdRef.current) return;
    const prevStickies = stickiesRef.current;
    const prevLabels   = textLabelsRef.current;
    const peeked = peekUndo();
    if (!peeked) return;
    if (restoreTouchesForeignLock(occupancyRef.current, playerId, peeked.stickies, peeked.textLabels, prevStickies, prevLabels)) {
      setExportToast({ message: 'Can’t undo while someone else is editing those items', severity: 'info', open: true });
      return;
    }
    const entry = historyUndo(prevStickies, prevLabels);
    if (!entry) return;
    const restored = broadcastCanvasRestore(entry.stickies, entry.textLabels, prevStickies, prevLabels);
    setStickies(restored.stickies);
    setTextLabels(restored.textLabels);
    deselectAndRelease();
    track.event(RetroCategory.Canvas, RetroAction.UndoUsed);
  }, [broadcastCanvasRestore, deselectAndRelease, historyUndo, peekUndo, playerId, track]);

  const handleRedo = useCallback(() => {
    if (editingIdRef.current || labelEditingIdRef.current) return;
    const prevStickies = stickiesRef.current;
    const prevLabels   = textLabelsRef.current;
    const peeked = peekRedo();
    if (!peeked) return;
    if (restoreTouchesForeignLock(occupancyRef.current, playerId, peeked.stickies, peeked.textLabels, prevStickies, prevLabels)) {
      setExportToast({ message: 'Can’t redo while someone else is editing those items', severity: 'info', open: true });
      return;
    }
    const entry = historyRedo(prevStickies, prevLabels);
    if (!entry) return;
    const restored = broadcastCanvasRestore(entry.stickies, entry.textLabels, prevStickies, prevLabels);
    setStickies(restored.stickies);
    setTextLabels(restored.textLabels);
    deselectAndRelease();
    track.event(RetroCategory.Canvas, RetroAction.RedoUsed);
  }, [broadcastCanvasRestore, deselectAndRelease, historyRedo, peekRedo, playerId, track]);

  // End the retro: lock it to new participants and notify everyone. Moderator only.
  // The server broadcasts retroEnded back to the whole group (including this client).
  const handleEndRetro = useCallback(() => {
    new HttpClient(authToken)
      .post(`/retro-rooms/${roomUrlId}/end`, { playerId } as unknown as Parameters<HttpClient['post']>[1])
      .catch(console.error);
    track.event(RetroCategory.Moderation, RetroAction.RetroEnded);
  }, [authToken, roomUrlId, playerId, track]);

  // Reopen a previously closed retro. The server broadcasts retroReopened to all clients.
  const handleReopenRetro = useCallback(() => {
    new HttpClient(authToken)
      .post(`/retro-rooms/${roomUrlId}/reopen`, { playerId } as unknown as Parameters<HttpClient['post']>[1])
      .catch(console.error);
    track.event(RetroCategory.Moderation, RetroAction.RetroReopened);
  }, [authToken, roomUrlId, playerId, track]);

  // Keep the app-bar RetroMenu in sync with live undo/redo and export state.
  const hasSelection = selectedIds.size > 0;
  useEffect(() => {
    registerRetroActions({
      canUndo,
      canRedo,
      onUndo: handleUndo,
      onRedo: handleRedo,
      onExportMarkdown: () => { handleExportAll('markdown').catch(console.error); },
      onExportPlainText: () => { handleExportAll('text').catch(console.error); },
      canExportSelection: hasSelection,
      onExportSelectionMarkdown: () => { handleExportSelection('markdown').catch(console.error); },
      onExportSelectionPlainText: () => { handleExportSelection('text').catch(console.error); },
      onExportBoardAsImage: () => { handleExportBoardAsImage().catch(console.error); },
      onExportSelectionAsImage: () => { handleExportSelectionAsImage().catch(console.error); },
      canEndRetro: isModerator && !isRetroLocked,
      onEndRetro: handleEndRetro,
      canReopenRetro: isModerator && isRetroLocked,
      onReopenRetro: handleReopenRetro,
    });
  }, [canUndo, canRedo, handleUndo, handleRedo, handleExportAll, handleExportSelection, hasSelection, registerRetroActions, handleExportBoardAsImage, handleExportSelectionAsImage, isModerator, isRetroLocked, handleEndRetro, handleReopenRetro]);

  // Deregister when the canvas unmounts (e.g. navigating away).
  useEffect(() => () => unregisterRetroActions(), [unregisterRetroActions]);

  // --- Object copy / cut / paste / duplicate ---
  // Uses an internal ref clipboard (not the system clipboard) so object copy/paste
  // is independent of the existing text-export shortcuts (Ctrl+Shift+C/M).
  // Repeated pastes accumulate a +PASTE_BOARD_OFFSET so items never stack exactly.

  /** Returns the stickies and labels that are currently selected (single or multi). */
  const getSelectedItems = useCallback(() => {
    const selectedStickies = stickiesRef.current.filter(s => selectedIdsRef.current.has(s.id));
    // Merge multi-selection set with the single-focus label (selectedLabelId)
    const labelIdSet = new Set(selectedLabelIdsRef.current);
    if (selectedLabelIdRef.current) labelIdSet.add(selectedLabelIdRef.current);
    const selectedLabels = textLabelsRef.current.filter(l => labelIdSet.has(l.id));
    return { stickies: selectedStickies, labels: selectedLabels };
  }, []);

  /**
   * Insert cloned items onto the canvas, broadcast them to collaborators,
   * persist, and select the new items. Pushes the pre-insert state onto the
   * undo stack only when the insert actually happens, so a rejected
   * paste/duplicate never leaves a no-op entry in undo history. Returns
   * whether the items were inserted, so callers can gate success feedback
   * (toast, analytics) on the outcome instead of assuming success.
   */
  const insertClonedItems = useCallback((
    newStickies: StickyState[],
    newLabels: TextLabelState[],
  ): boolean => {
    const nextStickies = [...stickiesRef.current, ...newStickies];
    const nextLabels   = [...textLabelsRef.current, ...newLabels];
    if (wouldExceedCanvasCaps(nextStickies.length, nextLabels.length)) {
      setExportToast({ message: 'Pasting would exceed the board limit', severity: 'error', open: true });
      return false;
    }
    history.pushHistory(stickiesRef.current, textLabelsRef.current);
    stickiesRef.current = nextStickies;
    textLabelsRef.current = nextLabels;
    setStickies(nextStickies);
    setTextLabels(nextLabels);
    persistCommit(newStickies, newLabels);
    setSelectedIds(new Set(newStickies.map(s => s.id)));
    setSelectedLabelIds(new Set(newLabels.map(l => l.id)));
    setSelectedLabelId(newLabels.length === 1 && newStickies.length === 0 ? newLabels[0].id : null);
    syncOccupancyToSelection(
      newStickies.map(s => s.id),
      newLabels.map(l => l.id),
    );
    return true;
  }, [history, persistCommit, syncOccupancyToSelection]);

  const handleCopy = useCallback(() => {
    const { stickies: sel, labels: selLabels } = getSelectedItems();
    if (sel.length === 0 && selLabels.length === 0) return;
    clipboardRef.current = { stickies: sel, textLabels: selLabels };
    pasteOffsetCountRef.current = 0;
    setHasClipboard(true);
    track.event(RetroCategory.Canvas, RetroAction.CopyUsed);
  }, [getSelectedItems, track]);

  const handlePaste = useCallback(() => {
    const clipboard = clipboardRef.current;
    if (!clipboard || (clipboard.stickies.length === 0 && clipboard.textLabels.length === 0)) return;

    pasteOffsetCountRef.current += 1;
    const offset = pasteOffsetCountRef.current * PASTE_BOARD_OFFSET;
    const now = new Date().toISOString();

    let z = nextZIndex(stickiesRef.current, textLabelsRef.current);
    const newStickies: StickyState[] = clipboard.stickies.map(s => ({
      ...s, id: uuidv4(), x: s.x + offset, y: s.y + offset, zIndex: z++,
      authorId: playerId, authorName: playerName, createdAt: now, updatedAt: now,
    }));
    const newLabels: TextLabelState[] = clipboard.textLabels.map(l => ({
      ...l, id: uuidv4(), x: l.x + offset, y: l.y + offset, zIndex: z++,
      authorId: playerId, authorName: playerName, createdAt: now, updatedAt: now,
    }));

    if (!insertClonedItems(newStickies, newLabels)) return;

    const count = newStickies.length + newLabels.length;
    setExportToast({ message: `Pasted ${count} item${count !== 1 ? 's' : ''}`, severity: 'success', open: true });
    track.event(RetroCategory.Canvas, RetroAction.PasteUsed);
  }, [insertClonedItems, playerId, playerName, track]);

  const handleCut = useCallback(() => {
    const { stickies: sel, labels: selLabels } = getSelectedItems();
    if (sel.length === 0 && selLabels.length === 0) return;
    if (anyLockedByOther(sel.map(s => s.id), selLabels.map(l => l.id))) return;

    clipboardRef.current = { stickies: sel, textLabels: selLabels };
    pasteOffsetCountRef.current = 0;
    setHasClipboard(true);

    const stickyIds = sel.map(s => s.id);
    const labelIds  = selLabels.map(l => l.id);
    if (!occupancySessionRef.current.claimed(stickyIds, labelIds)) return;

    snapshotGestureOrigin();
    history.pushHistory(stickiesRef.current, textLabelsRef.current);

    const newStickies = stickiesRef.current.filter(s => !stickyIds.includes(s.id));
    const newLabels   = textLabelsRef.current.filter(l => !labelIds.includes(l.id));
    setStickies(newStickies);
    setTextLabels(newLabels);
    persistWhenHeld(stickyIds, labelIds, () => {
      persistDelete(stickyIds, labelIds);
      gestureOriginRef.current = null;
    });
    deselectAndRelease();
    track.event(RetroCategory.Canvas, RetroAction.CutUsed);
  }, [anyLockedByOther, deselectAndRelease, getSelectedItems, history, persistDelete, persistWhenHeld, snapshotGestureOrigin, track]);

  const handleDuplicate = useCallback(() => {
    const { stickies: sel, labels: selLabels } = getSelectedItems();
    if (sel.length === 0 && selLabels.length === 0) return;

    const now = new Date().toISOString();

    let z = nextZIndex(stickiesRef.current, textLabelsRef.current);
    const newStickies: StickyState[] = sel.map(s => ({
      ...s, id: uuidv4(), x: s.x + PASTE_BOARD_OFFSET, y: s.y + PASTE_BOARD_OFFSET, zIndex: z++,
      authorId: playerId, authorName: playerName, createdAt: now, updatedAt: now,
    }));
    const newLabels: TextLabelState[] = selLabels.map(l => ({
      ...l, id: uuidv4(), x: l.x + PASTE_BOARD_OFFSET, y: l.y + PASTE_BOARD_OFFSET, zIndex: z++,
      authorId: playerId, authorName: playerName, createdAt: now, updatedAt: now,
    }));

    if (!insertClonedItems(newStickies, newLabels)) return;
    track.event(RetroCategory.Canvas, RetroAction.DuplicateUsed);
  }, [getSelectedItems, insertClonedItems, playerId, playerName, track]);

  // Keys 1–6 change the selected item's color (only when not editing).
  // Delete/Backspace with multiple stickies selected removes all of them.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      // Undo / Redo — highest priority so they work regardless of selection state
      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        if (e.key === 'z' || e.key === 'Z') {
          if (e.shiftKey) {
            e.preventDefault();
            handleRedo();
            return;
          }
          e.preventDefault();
          handleUndo();
          return;
        }
        if ((e.key === 'y' || e.key === 'Y') && !e.shiftKey) {
          e.preventDefault();
          handleRedo();
          return;
        }
      }

      // Copy / Cut / Paste / Duplicate
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
        if (e.key === 'c' || e.key === 'C') {
          e.preventDefault();
          handleCopy();
          return;
        }
        if (e.key === 'x' || e.key === 'X') {
          e.preventDefault();
          handleCut();
          return;
        }
        if (e.key === 'v' || e.key === 'V') {
          e.preventDefault();
          handlePaste();
          return;
        }
        if (e.key === 'd' || e.key === 'D') {
          e.preventDefault();
          handleDuplicate();
          return;
        }
      }

      // Export shortcuts — fire at any time when not editing text
      if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
        if (e.key === 'M' || e.key === 'm') {
          e.preventDefault();
          handleExportAutoScope('markdown').catch(console.error);
          return;
        }
        if (e.key === 'C' || e.key === 'c') {
          e.preventDefault();
          handleExportAutoScope('text').catch(console.error);
          return;
        }
      }

      // Escape cancels placement mode
      if (e.key === 'Escape' && placementModeRef.current) {
        setPlacementMode(null);
        return;
      }

      // N / T — enter placement mode when nothing is selected and not editing
      if (!selectedIdsRef.current.size && !selectedLabelIdsRef.current.size && !selectedLabelIdRef.current && !editingIdRef.current && !labelEditingId) {
        if (e.key === 'n' || e.key === 'N') {
          e.preventDefault();
          setPlacementMode('sticky');
          return;
        }
        if (e.key === 't' || e.key === 'T') {
          e.preventDefault();
          setPlacementMode('label');
          return;
        }
      }

      // Bulk delete — fires before the single-select guard so it works with 2+ selected items
      if ((e.key === 'Delete' || e.key === 'Backspace') &&
          (selectedIdsRef.current.size + selectedLabelIdsRef.current.size) > 1 &&
          !editingIdRef.current) {
        e.preventDefault();
        const stickyIds = Array.from(selectedIdsRef.current);
        const labelIds  = Array.from(selectedLabelIdsRef.current);
        if (anyLockedByOther(stickyIds, labelIds)) return;
        if (!occupancySessionRef.current.claimed(stickyIds, labelIds)) return;
        snapshotGestureOrigin();
        const newStickies = stickiesRef.current.filter(s => !stickyIds.includes(s.id));
        const newLabels   = textLabelsRef.current.filter(l => !labelIds.includes(l.id));
        history.pushHistory(stickiesRef.current, textLabelsRef.current);
        setStickies(newStickies);
        setTextLabels(newLabels);
        persistWhenHeld(stickyIds, labelIds, () => {
          persistDelete(stickyIds, labelIds);
          gestureOriginRef.current = null;
        });
        deselectAndRelease();
        if (stickyIds.length > 0) track.event(RetroCategory.Canvas, RetroAction.StickyDeleted, 'bulk');
        if (labelIds.length > 0) track.event(RetroCategory.Canvas, RetroAction.TextLabelDeleted, 'bulk');
        return;
      }

      // Selected text label keyboard shortcuts
      if (selectedLabelIdRef.current && !labelEditingId) {
        const labelId = selectedLabelIdRef.current;

        if (e.key === 'Enter') {
          e.preventDefault();
          if (!anyLockedByOther([], [labelId])) setLabelEditTriggerId(labelId);
          return;
        }
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          handleLabelDelete(labelId);
          return;
        }
        if (e.key === '[') {
          e.preventDefault();
          resizeLabel(labelId, -LABEL_FONT_STEP);
          return;
        }
        if (e.key === ']') {
          e.preventDefault();
          resizeLabel(labelId, LABEL_FONT_STEP);
          return;
        }
        const colorIdx = parseInt(e.key, 10) - 1;
        if (colorIdx >= 0 && colorIdx < labelColors.length) {
          handleLabelChange({ id: labelId, colorSlot: colorIdx });
          return;
        }
        // Printable character → enter label edit mode
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault();
          if (!anyLockedByOther([], [labelId])) setLabelEditTriggerId(labelId);
        }
        return;
      }

      if (!selectedIdRef.current || editingIdRef.current) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        if (!anyLockedByOther([selectedIdRef.current], [])) setEditTriggerId(selectedIdRef.current);
        return;
      }

      if (e.key === '[') {
        e.preventDefault();
        resizeSticky(selectedIdRef.current, -SIZE_STEP);
        return;
      }
      if (e.key === ']') {
        e.preventDefault();
        resizeSticky(selectedIdRef.current, SIZE_STEP);
        return;
      }

      const idx = parseInt(e.key, 10) - 1;
      if (idx >= 0 && idx < STICKY_COLORS.length) {
        handleStickyChange({ id: selectedIdRef.current, colorSlot: idx });
        track.event(RetroCategory.Canvas, RetroAction.StickyColorChanged, 'keyboard');
        return;
      }

      // Printable character → enter edit mode (same as text labels — keep existing content)
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        if (!anyLockedByOther([selectedIdRef.current], [])) setEditTriggerId(selectedIdRef.current);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [anyLockedByOther, deselectAndRelease, handleCopy, handleCut, handleDuplicate, handleExportAutoScope, handleLabelChange, handleLabelDelete, handlePaste, handleRedo, handleStickyChange, handleUndo, history, labelEditingId, persistDelete, persistWhenHeld, resizeLabel, resizeSticky, snapshotGestureOrigin, track]);

  // --- Canvas double-click to add sticky ---

  const handleCanvasDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).dataset.canvas !== 'true') return;
      if (isPanModeRef.current) return;

      // Suppress if the pointer dragged more than the threshold (panning ended with dblclick)
      if (pointerDownPosRef.current) {
        const dx = e.clientX - pointerDownPosRef.current.x;
        const dy = e.clientY - pointerDownPosRef.current.y;
        if (Math.hypot(dx, dy) > POINTER_DRAG_THRESHOLD_PX) return;
      }

      const transform = transformRef.current?.state;
      if (!transform) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const { x: boardX, y: boardY } = screenToBoard(
        e.clientX - rect.left,
        e.clientY - rect.top,
        transform,
      );

      const newId = addSticky(boardX, boardY);
      if (newId) setEditTriggerId(newId);
    },
    [addSticky]
  );

  // Pointer down on canvas background — clear selection and begin drawing lasso rect
  const handleCanvasPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).dataset.canvas !== 'true') return;
      const active = document.activeElement as HTMLElement | null;
      if (active?.isContentEditable) active.blur();
      pointerDownPosRef.current = { x: e.clientX, y: e.clientY };
      setContextMenuPos(null);

      if (!isPanModeRef.current && e.button === 0) {
        const transform = transformRef.current?.state;
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (transform && containerRect) {
          const board = screenToBoard(e.clientX - containerRect.left, e.clientY - containerRect.top, transform);
          const rect = { startX: board.x, startY: board.y, curX: board.x, curY: board.y };
          deselectAndRelease();
          setSelectionRect(rect);
          selectionRectRef.current = rect;
          e.currentTarget.setPointerCapture(e.pointerId);
          return;
        }
      }
      deselectAndRelease();
    },
    [deselectAndRelease],
  );

  // Pointer move — update the lasso rect and live-select intersecting stickies
  const handleCanvasPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!selectionRectRef.current) return;
      const transform = transformRef.current?.state;
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!transform || !containerRect) return;

      const board = screenToBoard(e.clientX - containerRect.left, e.clientY - containerRect.top, transform);
      const newRect = { ...selectionRectRef.current, curX: board.x, curY: board.y };
      setSelectionRect(newRect);
      selectionRectRef.current = newRect;

      const selX = Math.min(newRect.startX, newRect.curX);
      const selY = Math.min(newRect.startY, newRect.curY);
      const selW = Math.abs(newRect.curX - newRect.startX);
      const selH = Math.abs(newRect.curY - newRect.startY);

      const newSelectedIds      = new Set<string>();
      const newSelectedLabelIds = new Set<string>();
      // Only hit-test once the rect has a meaningful size to avoid accidental selections
      if (selW >= 4 || selH >= 4) {
        stickiesRef.current.forEach(s => {
          const { w: sw, h: sh } = stickyDisplaySize(s.width, s.height, s.shape);
          if (rectsIntersect({ x: selX, y: selY, w: selW, h: selH }, { x: s.x, y: s.y, w: sw, h: sh })
              && !isOccupiedByOther(occupancyRef.current, 'sticky', s.id, playerId)) {
            newSelectedIds.add(s.id);
          }
        });
        textLabelsRef.current.forEach(l => {
          const el = labelRefsRef.current.get(l.id);
          const lw = el ? el.clientWidth  : 0;
          const lh = el ? el.clientHeight : 0;
          if (lw > 0 && lh > 0 && rectsIntersect({ x: selX, y: selY, w: selW, h: selH }, { x: l.x, y: l.y, w: lw, h: lh })
              && !isOccupiedByOther(occupancyRef.current, 'label', l.id, playerId)) {
            newSelectedLabelIds.add(l.id);
          }
        });
      }
      setSelectedIds(newSelectedIds);
      setSelectedLabelIds(newSelectedLabelIds);
    },
    [playerId]
  );

  // Pointer up — finalise the lasso selection
  const handleCanvasPointerUp = useCallback(
    () => {
      if (selectionRectRef.current && (selectedIdsRef.current.size + selectedLabelIdsRef.current.size) > 1) {
        track.event(RetroCategory.Canvas, RetroAction.MultiSelectUsed, 'lasso');
        bringToFront(
          Array.from(selectedIdsRef.current),
          Array.from(selectedLabelIdsRef.current),
        );
      }
      if (selectionRectRef.current) {
        syncOccupancyToSelection(
          Array.from(selectedIdsRef.current),
          Array.from(selectedLabelIdsRef.current),
        );
      }
      setSelectionRect(null);
      selectionRectRef.current = null;
    },
    [bringToFront, syncOccupancyToSelection, track]
  );

  // Mouse move → broadcast cursor position in board space
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const transform = transformRef.current?.state;
      if (!transform) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const { x: boardX, y: boardY } = screenToBoard(
        e.clientX - rect.left,
        e.clientY - rect.top,
        transform,
      );
      broadcastCursor(boardX, boardY);
    },
    [broadcastCursor]
  );

  const handlePointerLeave = useCallback(() => {
    broadcastCursorLeft();
  }, [broadcastCursorLeft]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
  }, []);

  // SVG cursors for placement mode — URL-encoded for use in CSS `cursor` property.
  // Hotspot for the sticky is centered (14,14); for the label it is top-left (2,2).
  // Re-computed when the theme mode changes so colors stay visible on both backgrounds.
  const CURSOR_STICKY = useMemo(() => {
    const stroke = theme.palette.mode === 'dark' ? '%23aaa' : '%23888';
    return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28'%3E%3Crect x='2' y='2' width='22' height='22' rx='2' fill='%23fff9b1' stroke='${stroke}' stroke-width='1.5'/%3E%3Cline x1='7' y1='11' x2='21' y2='11' stroke='%23ccc' stroke-width='1.5'/%3E%3Cline x1='7' y1='15' x2='21' y2='15' stroke='%23ccc' stroke-width='1.5'/%3E%3Cline x1='7' y1='19' x2='18' y2='19' stroke='%23ccc' stroke-width='1.5'/%3E%3C/svg%3E") 14 14, crosshair`;
  }, [theme.palette.mode]);

  const CURSOR_LABEL = useMemo(() => {
    const fill = theme.palette.mode === 'dark' ? '%23e0e0e0' : '%231a1a1a';
    return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='26' height='28'%3E%3Ctext x='2' y='22' font-size='22' font-weight='700' fill='${fill}' font-family='Arial%2CSans-Serif'%3ET%3C/text%3E%3Cline x1='2' y1='26' x2='24' y2='26' stroke='${fill}' stroke-width='2'/%3E%3C/svg%3E") 2 2, text`;
  }, [theme.palette.mode]);

  if (retroEnded) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          px: 3,
          background: ct.background,
        }}
      >
        <LockIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="h5" align="center">This retro has ended</Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ maxWidth: 480 }}>
          The board is now closed to new participants. If you were signed in, you can reopen it from
          your recent retros at any time.
        </Typography>
      </Box>
    );
  }

  // Match SpeedDialAction Fab hover/press (MUI uses emphasize(paper, 0.15) on hover).
  const actionFabHoverBg = emphasize(theme.palette.background.paper, 0.15);
  const actionFabActiveBg = emphasize(theme.palette.background.paper, 0.25);
  const speedDialActionSx = {
    '& .MuiSpeedDialAction-staticTooltipLabel': {
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      // Keep open/close fade only — background must snap like the action Fab.
      transition: theme.transitions.create(['transform', 'opacity'], {
        duration: theme.transitions.duration.shorter,
      }),
    },
    '&:hover .MuiSpeedDialAction-staticTooltipLabel, &:hover .MuiFab-root': {
      backgroundColor: actionFabHoverBg,
    },
    '&:active .MuiSpeedDialAction-staticTooltipLabel, &:active .MuiFab-root': {
      backgroundColor: actionFabActiveBg,
    },
  };

  // preventDefault on pointerdown so contenteditable doesn't blur before click
  // (blur → re-render would swallow the gesture). onClick still handles mouse + keyboard.
  const handleSpeedDialAddSticky = useCallback(() => {
    setSpeedDialOpen(false);
    setPlacementMode('sticky');
    blurActiveContentEditable();
  }, []);

  const handleSpeedDialAddLabel = useCallback(() => {
    setSpeedDialOpen(false);
    setPlacementMode('label');
    blurActiveContentEditable();
  }, []);

  const handleSpeedDialTemplate = useCallback(() => {
    setSpeedDialOpen(false);
    setTemplatePickerOpen(true);
    track.event(RetroCategory.Template, RetroAction.TemplatePickerOpened);
    blurActiveContentEditable();
  }, [track]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100vw',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
        position: 'relative',
        background: ct.background,
        cursor: isPanMode ? 'grab' : 'default',
        '&:active': isPanMode ? { cursor: 'grabbing' } : {},
      }}
      onMouseMove={handleMouseMove}
      onPointerLeave={handlePointerLeave}
      onContextMenu={handleContextMenu}
    >
      <TransformWrapper
        ref={transformRef}
        initialScale={1}
        minScale={0.1}
        maxScale={4}
        limitToBounds
        centerZoomedOut
        disablePadding
        wheel={{ disabled: true }}
        doubleClick={{ disabled: true }}
        // Left-click drag panning is intentionally disabled.
        // Pan is handled by: two-finger scroll (wheel handler), Space+drag (isPanMode),
        // and middle mouse button drag (pointer handler below).
        panning={{ disabled: !isPanMode, excluded: ['sticky-note', 'text-label'] }}
        onInit={() => {
          const saved = loadViewport(roomUrlId);
          if (saved) {
            setClampedTransform(saved.x, saved.y, saved.scale);
            return;
          }
          if (initialTemplateId) {
            fitTemplateViewport(initialTemplateId);
          } else {
            fitViewport(initialStickies);
          }
        }}
        onPanning={(ref) => {
          setClampedTransform(ref.state.positionX, ref.state.positionY, ref.state.scale, {
            onlyIfChanged: true,
            instance: ref,
          });
        }}
        onPanningStop={(ref) => {
          const vp = setClampedTransform(ref.state.positionX, ref.state.positionY, ref.state.scale, {
            onlyIfChanged: true,
            instance: ref,
          });
          if (vp) saveViewport(roomUrlId, vp);
        }}
      >
        {({ state }) => (
          <>
          <TransformComponent
            wrapperStyle={{ width: '100%', height: '100%' }}
            contentStyle={{ width: `${CANVAS_SIZE}px`, height: `${CANVAS_SIZE}px`, position: 'relative' }}
          >
            {/* Dot-grid background */}
            <Box
              data-canvas="true"
              onDoubleClick={handleCanvasDoubleClick}
              onPointerDown={handleCanvasPointerDown}
              onPointerMove={handleCanvasPointerMove}
              onPointerUp={handleCanvasPointerUp}
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage:
                  `radial-gradient(circle, ${ct.gridDot} 1px, transparent 1px)`,
                backgroundSize: '32px 32px',
                width: '100%',
                height: '100%',
              }}
            />

            {templateId && (
              <TemplateLayer ref={templateLayerRef} templateId={templateId} isDarkMode={isDarkMode} />
            )}

            {/* Lasso selection rectangle */}
            {selectionRect && (() => {
              const x = Math.min(selectionRect.startX, selectionRect.curX);
              const y = Math.min(selectionRect.startY, selectionRect.curY);
              const w = Math.abs(selectionRect.curX - selectionRect.startX);
              const h = Math.abs(selectionRect.curY - selectionRect.startY);
              return (
                <Box sx={{
                  position: 'absolute',
                  left: x,
                  top: y,
                  // MUI's sx sizing transform treats any bare number <= 1 (and != 0) as a
                  // *fraction* of the parent and converts it to a percentage — e.g. a 0.3px
                  // wide box becomes "30%" of the 20 000px canvas (~6 000px). That happens
                  // for a moment every time the drag crosses back over its own start point
                  // on an axis, since the delta briefly dips below 1 board-px. Passing
                  // explicit px strings opts out of that transform entirely.
                  width: `${w}px`,
                  height: `${h}px`,
                  border: `1.5px solid ${ct.selectionColor}`,
                  background: ct.selectionFill,
                  borderRadius: '2px',
                  pointerEvents: 'none',
                  zIndex: 50,
                }} />
              );
            })()}

            {/* Group selection bounding box — shown while 2+ stickies are selected.
                 Rendered below the selected items so they still capture pointer events.
                 Clicks in the gap area inside the box initiate a group drag.
                 Hidden while drawing a new lasso rect. */}
            {groupBounds && !selectionRect && (
              <Box
                ref={groupBoundsElRef}
                onPointerDown={handleGroupPointerDown}
                onPointerMove={handleGroupPointerMove}
                onPointerUp={handleGroupPointerUp}
                onPointerCancel={handleGroupPointerUp}
                onLostPointerCapture={handleGroupPointerUp}
                sx={{
                  position:    'absolute',
                  left:         groupBounds.x - 8,
                  top:          groupBounds.y - 8,
                  width:        groupBounds.w + 16,
                  height:       groupBounds.h + 16,
                  border:      `1.5px dashed ${ct.lassoColor}`,
                  borderRadius: '4px',
                  background:  'transparent',
                  cursor:       isGroupDragging ? 'grabbing' : 'grab',
                  zIndex:       groupBounds.zIndex,
                  touchAction: 'none',
                }}
              />
            )}

            {/* Empty-canvas hint — centred at the canvas midpoint */}
            {stickies.length === 0 && textLabels.length === 0 && (
              <Box
                sx={{
                  position:   'absolute',
                  left:        CANVAS_SIZE / 2,
                  top:         CANVAS_SIZE / 2,
                  transform:  'translate(-50%, -50%)',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  display:    'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap:        1,
                }}
              >
                <Box sx={{
                  fontSize:   15,
                  fontFamily: 'sans-serif',
                  color:      ct.hintText,
                  fontWeight: 500,
                  letterSpacing: '0.01em',
                }}>
                  Double-click anywhere to add a sticky note
                </Box>
                <Box sx={{
                  fontSize:   12,
                  fontFamily: 'sans-serif',
                  color:      ct.hintSubText,
                }}>
                  Two-finger scroll to pan · Pinch to zoom · Space+drag to pan
                </Box>
              </Box>
            )}

            {/* Stickies */}
            {stickies.map(sticky => {
              const { w: stickyW } = stickyDisplaySize(sticky.width, sticky.height, sticky.shape);
              const stickyOccupiedBy = occupiedByName(occupancy, 'sticky', sticky.id, playerId);
              const showStickyResize =
                selectedId === sticky.id &&
                !(editingId === sticky.id && editHasText) &&
                draggingId !== sticky.id &&
                !stickyOccupiedBy;
              return (
              <StickyNote
                key={sticky.id}
                ref={(el) => {
                  if (el) stickyRefsRef.current.set(sticky.id, el);
                  else stickyRefsRef.current.delete(sticky.id);
                }}
                sticky={sticky}
                isSelected={selectedIds.has(sticky.id)}
                isMultiSelected={selectedIds.size > 1 && selectedIds.has(sticky.id)}
                onSelect={(id: string) => {
                  setSelectedLabelId(null);
                  setSelectedLabelIds(new Set());
                  setSelectedIds(new Set([id]));
                  bringToFront([id], []);
                  syncOccupancyToSelection([id], []);
                }}
                onChange={handleStickyChange}
                onDragEnd={handleDragEnd}
                onDelete={handleDelete}
                onEditingChange={handleEditingChange}
                onDraggingChange={handleDraggingChange}
                onHasTextChange={handleHasTextChange}
                onMultiDragMove={handleMultiDragMove}
                onMultiDragEnd={handleMultiDragEnd}
                scale={state.scale}
                isPanMode={isPanMode}
                shouldStartEdit={editTriggerId === sticky.id}
                selectionColor={ct.selectionColor}
                occupiedByName={stickyOccupiedBy}
                occupancyAbortEpoch={occupancyAbortEpoch}
                {...makeResizeHandleProps(
                  showStickyResize,
                  finishStickyResize,
                  (corner, e) => ({
                    corner,
                    startClientX: e.clientX, startClientY: e.clientY,
                    startStickyX: sticky.x,  startStickyY: sticky.y,
                    startSize: stickyW, scale: state.scale, stickyId: sticky.id,
                  }),
                  (rs, clientX, clientY) => {
                    const { newX, newY, newSize } = computeResize(rs, clientX, clientY);
                    setStickies(prev => {
                      const idx = prev.findIndex(st => st.id === rs.stickyId);
                      if (idx === -1) return prev;
                      const cur = prev[idx];
                      if (cur.x === newX && cur.y === newY && cur.width === newSize && cur.height === newSize) {
                        return prev;
                      }
                      const next = [...prev];
                      next[idx] = { ...cur, x: newX, y: newY, width: newSize, height: newSize };
                      return next;
                    });
                  },
                )}
              />
              );
            })}

            {/* Text labels */}
            {textLabels.map(label => {
              const font = label.fontSize ?? DEFAULT_LABEL_FONT;
              const labelOccupiedBy = occupiedByName(occupancy, 'label', label.id, playerId);
              const showLabelResize =
                selectedLabelId === label.id &&
                !(labelEditingId === label.id && labelEditHasText) &&
                labelDraggingId !== label.id &&
                !labelOccupiedBy;
              return (
              <TextLabel
                key={label.id}
                ref={(el) => {
                  if (el) labelRefsRef.current.set(label.id, el);
                  else labelRefsRef.current.delete(label.id);
                }}
                label={label}
                isSelected={selectedLabelId === label.id || selectedLabelIds.has(label.id)}
                isMultiSelected={selectedLabelIds.size > 0 && selectedLabelIds.has(label.id)}
                onSelect={(id) => {
                  setSelectedIds(new Set());
                  setSelectedLabelIds(new Set());
                  setSelectedLabelId(id);
                  bringToFront([], [id]);
                  syncOccupancyToSelection([], [id]);
                }}
                onChange={handleLabelChange}
                onDragEnd={handleLabelDragEnd}
                onDelete={handleLabelDelete}
                onEditingChange={handleLabelEditingChange}
                onDraggingChange={handleLabelDraggingChange}
                onHasTextChange={handleLabelHasTextChange}
                onMultiDragMove={handleMultiDragMove}
                onMultiDragEnd={handleMultiDragEnd}
                scale={state.scale}
                isPanMode={isPanMode}
                shouldStartEdit={labelEditTriggerId === label.id}
                selectionColor={ct.selectionColor}
                labelColors={labelColors}
                occupiedByName={labelOccupiedBy}
                occupancyAbortEpoch={occupancyAbortEpoch}
                {...makeResizeHandleProps(
                  showLabelResize,
                  finishLabelResize,
                  (corner, e) => {
                    const el = labelRefsRef.current.get(label.id);
                    return {
                      corner,
                      startClientX: e.clientX, startClientY: e.clientY,
                      startStickyX: label.x,   startStickyY: label.y,
                      startSize: font,
                      scale: state.scale, stickyId: label.id,
                      startElemWidth:  el ? el.clientWidth  : undefined,
                      startElemHeight: el ? el.clientHeight : undefined,
                    };
                  },
                  (rs, clientX, clientY) => {
                    const { newFont, newX, newY } = computeLabelFontResize(rs, clientX, clientY);
                    setTextLabels(prev => {
                      const idx = prev.findIndex(l => l.id === rs.stickyId);
                      if (idx === -1) return prev;
                      const cur = prev[idx];
                      if (cur.x === newX && cur.y === newY && cur.fontSize === newFont) {
                        return prev;
                      }
                      const next = [...prev];
                      next[idx] = { ...cur, x: newX, y: newY, fontSize: newFont };
                      return next;
                    });
                  },
                )}
              />
              );
            })}

            {/* Live cursors (other participants) */}
            <CursorOverlay ref={cursorOverlayRef} currentPlayerId={playerId} />
          </TransformComponent>

          {/* Sticky color strip — fixed in screen space, appears below the selected sticky.
               Hidden while editing, dragging, resizing, or panning (which would make it lag). */}
          {(() => {
            if (!selectedId || (editingId === selectedId && editHasText) || draggingId === selectedId || isResizing || isPanMode || isOccupiedByOther(occupancy, 'sticky', selectedId, playerId)) return null;
            const sticky = stickies.find(s => s.id === selectedId);
            if (!sticky) return null;
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return null;
            const { w: stickyW, h: stickyH } = stickyDisplaySize(sticky.width, sticky.height, sticky.shape);
            const screen = boardToScreen(sticky.x + stickyW / 2, sticky.y + stickyH, state);
            return (
              <CanvasColorStrip
                stripX={rect.left + screen.x}
                stripY={rect.top  + screen.y + 10}
                colors={STICKY_COLORS}
                selectedSlot={sticky.colorSlot}
                onChange={(slot) => {
                  handleStickyChange({ id: selectedId, colorSlot: slot });
                  track.event(RetroCategory.Canvas, RetroAction.StickyColorChanged, 'strip');
                }}
                isTransforming={isTransforming}
                bg={ct.colorStripBg}
                border={ct.colorStripBorder}
              />
            );
          })()}

          {/* Text label color strip — fixed in screen space, appears below the selected label.
               Hidden while editing with text, dragging, resizing, or panning — mirrors the
               sticky color strip so newly created titles keep the picker visible until typing starts. */}
          {(() => {
            if (!selectedLabelId || (labelEditingId === selectedLabelId && labelEditHasText) || labelDraggingId === selectedLabelId || isResizing || isPanMode || isOccupiedByOther(occupancy, 'label', selectedLabelId, playerId)) return null;
            const label = textLabels.find(l => l.id === selectedLabelId);
            if (!label) return null;
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return null;
            const labelEl = labelRefsRef.current.get(selectedLabelId);
            const { w: labelBW, h: labelBH } = labelColorStripAnchorSize(label, labelEl);
            const screen  = boardToScreen(label.x + labelBW / 2, label.y + labelBH, state);
            return (
              <CanvasColorStrip
                stripX={rect.left + screen.x}
                stripY={rect.top  + screen.y + 10}
                colors={labelColors}
                selectedSlot={label.colorSlot}
                onChange={(slot) => handleLabelChange({ id: selectedLabelId, colorSlot: slot })}
                isTransforming={isTransforming}
                bg={ct.colorStripBg}
                border={ct.colorStripBorder}
              />
            );
          })()}

          </>
        )}
      </TransformWrapper>

      {/* Placement mode overlay — intercepts the next click to place an item.
           Sits above all canvas content (z-index 1001) so the cursor is consistent
           even when hovering over existing stickies or labels. Escape cancels. */}
      {placementMode && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1001,
            cursor: placementMode === 'sticky' ? CURSOR_STICKY : CURSOR_LABEL,
          }}
          onPointerDown={(e) => {
            // Only respond to primary button clicks
            if (e.button !== 0) return;
            const transform = transformRef.current?.state;
            const rect = containerRef.current?.getBoundingClientRect();
            if (!transform || !rect) return;
            const { x: boardX, y: boardY } = screenToBoard(
              e.clientX - rect.left,
              e.clientY - rect.top,
              transform,
            );
            setPlacementMode(null);
            if (placementMode === 'sticky') {
              const newId = addSticky(boardX, boardY);
              if (newId) setEditTriggerId(newId);
            } else {
              const newId = addTextLabel(boardX, boardY);
              if (newId) {
                // Start edit in this commit so TextLabel's useLayoutEffect runs
                // before paint. Waiting on the occupancy lock used to delay
                // shouldStartEdit until after the color strip had painted against
                // the unmeasured 200px fallback, which made the picker jump left.
                setLabelEditTriggerId(newId);
              }
            }
          }}
        />
      )}

      {/* SpeedDial — folds out to offer Sticky Note and Text Label */}
      <SpeedDial
        ref={speedDialRef}
        ariaLabel="Add canvas item"
        icon={<SpeedDialIcon />}
        open={speedDialOpen}
        onOpen={(_, reason) => {
          // Open only on FAB click (or Enter/Space) — not hover or focus.
          if (reason === 'toggle') setSpeedDialOpen(true);
        }}
        onClose={(_, reason) => {
          // Stay open on mouse leave; close on FAB click, outside click (blur), or Esc.
          if (reason === 'mouseLeave') return;
          setSpeedDialOpen(false);
        }}
        sx={{ position: 'absolute', bottom: 24, right: 24, zIndex: 10 }}
        FabProps={{
          color: 'primary',
          onPointerDown: preventSpeedDialFocusSteal,
        }}
      >
        <SpeedDialAction
          icon={<NoteAddIcon />}
          tooltipTitle={<SpeedDialLabel label="Sticky note" shortcut="N" />}
          tooltipOpen
          sx={speedDialActionSx}
          onPointerDown={preventSpeedDialFocusSteal}
          onClick={handleSpeedDialAddSticky}
        />
        <SpeedDialAction
          icon={<TitleIcon />}
          tooltipTitle={<SpeedDialLabel label="Text label" shortcut="T" />}
          tooltipOpen
          sx={speedDialActionSx}
          onPointerDown={preventSpeedDialFocusSteal}
          onClick={handleSpeedDialAddLabel}
        />
        {isModerator && (
          <SpeedDialAction
            icon={<DashboardCustomizeIcon />}
            tooltipTitle="Template"
            tooltipOpen
            sx={speedDialActionSx}
            onPointerDown={preventSpeedDialFocusSteal}
            onClick={handleSpeedDialTemplate}
          />
        )}
      </SpeedDial>

      {isModerator && (
        <TemplatePickerDialog
          open={templatePickerOpen}
          activeTemplateId={templateId}
          isDarkMode={isDarkMode}
          onClose={() => setTemplatePickerOpen(false)}
          onSelect={handleApplyTemplate}
        />
      )}

      {/* Right-click context menu */}
      <Menu
        open={contextMenuPos !== null}
        onClose={() => setContextMenuPos(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenuPos ? { top: contextMenuPos.y, left: contextMenuPos.x } : undefined}
      >
        {/* Object clipboard actions — shown when items are selected or clipboard is non-empty */}
        {(selectedIds.size > 0 || selectedLabelIds.size > 0 || selectedLabelId !== null || hasClipboard) && [
          selectedIds.size > 0 || selectedLabelIds.size > 0 || selectedLabelId !== null ? (
            <MenuItem
              key="copy"
              onClick={() => {
                setContextMenuPos(null);
                handleCopy();
              }}
            >
              Copy
            </MenuItem>
          ) : null,
          selectedIds.size > 0 || selectedLabelIds.size > 0 || selectedLabelId !== null ? (
            <MenuItem
              key="cut"
              onClick={() => {
                setContextMenuPos(null);
                handleCut();
              }}
            >
              Cut
            </MenuItem>
          ) : null,
          selectedIds.size > 0 || selectedLabelIds.size > 0 || selectedLabelId !== null ? (
            <MenuItem
              key="duplicate"
              onClick={() => {
                setContextMenuPos(null);
                handleDuplicate();
              }}
            >
              Duplicate
            </MenuItem>
          ) : null,
          hasClipboard ? (
            <MenuItem
              key="paste"
              onClick={() => {
                setContextMenuPos(null);
                handlePaste();
              }}
            >
              Paste
            </MenuItem>
          ) : null,
          <Divider key="obj-divider" />,
        ]}
        {selectedIds.size > 0 && [
          <MenuItem
            key="sel-md"
            onClick={() => {
              setContextMenuPos(null);
              const selected = stickiesRef.current.filter(s => selectedIdsRef.current.has(s.id));
              handleExport('markdown', selected, 'selection').catch(console.error);
            }}
          >
            Copy selection as Markdown
          </MenuItem>,
          <MenuItem
            key="sel-text"
            onClick={() => {
              setContextMenuPos(null);
              const selected = stickiesRef.current.filter(s => selectedIdsRef.current.has(s.id));
              handleExport('text', selected, 'selection').catch(console.error);
            }}
          >
            Copy selection as plain text
          </MenuItem>,
          <Divider key="divider" />,
        ]}
        <MenuItem
          onClick={() => {
            setContextMenuPos(null);
            handleExportAll('markdown').catch(console.error);
          }}
        >
          Copy all as Markdown
        </MenuItem>
        <MenuItem
          onClick={() => {
            setContextMenuPos(null);
            handleExportAll('text').catch(console.error);
          }}
        >
          Copy all as plain text
        </MenuItem>
      </Menu>

      {/* Export / info toast — duration=null makes it persistent until manually dismissed */}
      <Snackbar
        open={exportToast.open}
        autoHideDuration={exportToast.duration !== undefined ? exportToast.duration : 3000}
        onClose={() => setExportToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={exportToast.severity}
          onClose={() => setExportToast(prev => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {exportToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
